import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import FooterNav from "../components/FooterNav";
import {
    ArrowLeft,
    Check,
    ChevronDown,
    ChevronRight,
    Copy,
    Folder as FolderIcon,
    FolderPen,
    FolderPlus,
    Inbox,
    Loader2,
    MoreHorizontal,
    NotebookTabs,
    Pin,
    Plus,
    RotateCcw,
    Save,
    Search,
    Send,
    Share2,
    ScrollText,
    Tags,
    Trash2,
    UsersRound,
    X,
} from "lucide-react";
import {
    bulkCopyVaultItems,
    bulkDeleteVaultItems,
    bulkMoveVaultItems,
    bulkPinVaultItems,
    bulkRestoreVaultItems,
    bulkShareNotes,
    bulkTagNotes,
    bulkTrashVaultItems,
    createFolder as apiCreateFolder,
    createNote,
    deleteFolder,
    deleteNotePermanently,
    fetchFolders,
    fetchNote,
    fetchNotes,
    fetchVaultPreferences,
    saveVaultPreferences,
    togglePin,
    toggleTrash,
    updateFolder,
    updateNote,
    type CreateNoteInput,
    type FetchNotesParams,
    type Folder as FolderType,
    type Note,
} from "../api/notes";
import ModalShell from "../features/vault/VaultModalShell";
import { VaultBulkActionSheets, VaultBulkSelectionRail, type VaultBulkActionSheet } from "../features/vault/VaultBulkActions";
import {
    clearNewNoteDraft,
    readNewNoteDraft,
    readVaultQueryCache,
    readVaultSnapshot,
    vaultQueryKey,
    writeNewNoteDraft,
    writeVaultQueryCache,
    writeVaultSnapshot,
    type ContentView,
    type NoteType,
    type NoteTypeFilter,
    type SidebarFilter,
    type SortMode,
    type ViewMode,
    type VaultSnapshot,
} from "../features/vault/vaultState";
import {
    CONTENT_VIEW_OPTIONS,
    FolderTile,
    NoteCard,
    NoteSkeleton,
    OPEN_COLORS,
    OpenNotePane,
    PAGE_SIZE,
    RichEditor,
    SidebarButton,
    descendantFolderIds,
    isFolderFilter,
    normalizeUsernameSearch,
    notePreview,
    timeAgo,
} from "../features/vault/VaultParts";

type ConfirmState = {
    title: string;
    message: string;
    confirmLabel: string;
    secondaryLabel?: string;
    tone?: "danger" | "normal";
    onConfirm: () => Promise<void> | void;
    onSecondary?: () => Promise<void> | void;
};

const FALLBACK_SELECTION_ACCENT = "#22d3ee";
const FOLDER_BATCH_SIZE = 12;
const NOTE_BATCH_SIZE = 12;

function useMediaQuery(query: string) {
    const [matches, setMatches] = useState(() => (
        typeof window !== "undefined" ? window.matchMedia(query).matches : false
    ));

    useEffect(() => {
        if (typeof window === "undefined") return;
        const media = window.matchMedia(query);
        const update = () => setMatches(media.matches);
        update();
        media.addEventListener("change", update);
        return () => media.removeEventListener("change", update);
    }, [query]);

    return matches;
}

function normalizeVisibleVaultFilter(filter?: SidebarFilter | null): SidebarFilter {
    return filter === "unfiled" ? "all" : (filter || "all");
}

function normalizeHexColor(value?: string | null) {
    const color = String(value || "").trim();
    if (/^#[0-9a-f]{6}$/i.test(color)) return color;
    return null;
}

function blendHexColors(colors: Array<string | null | undefined>) {
    const validColors = colors.map(normalizeHexColor).filter((color): color is string => Boolean(color));
    if (validColors.length === 0) return FALLBACK_SELECTION_ACCENT;
    const total = validColors.reduce(
        (acc, color) => {
            acc.r += parseInt(color.slice(1, 3), 16);
            acc.g += parseInt(color.slice(3, 5), 16);
            acc.b += parseInt(color.slice(5, 7), 16);
            return acc;
        },
        { r: 0, g: 0, b: 0 }
    );
    const toHex = (value: number) => Math.round(value / validColors.length).toString(16).padStart(2, "0");
    return `#${toHex(total.r)}${toHex(total.g)}${toHex(total.b)}`;
}

function VaultGlowBookIcon() {
    return (
        <svg className="bs-vault-glow-book" viewBox="0 0 64 64" aria-hidden="true" focusable="false">
            <defs>
                <linearGradient id="vaultGlowBookStroke" x1="10" y1="8" x2="54" y2="56" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#f0abfc" />
                    <stop offset="0.52" stopColor="#d946ef" />
                    <stop offset="1" stopColor="#8b5cf6" />
                </linearGradient>
            </defs>
            <path className="bs-vault-glow-book-cover" stroke="url(#vaultGlowBookStroke)" d="M23 12h23a6 6 0 0 1 6 6v34H23a7 7 0 0 1-7-7V19a7 7 0 0 1 7-7Z" />
            <path className="bs-vault-glow-book-spine" stroke="url(#vaultGlowBookStroke)" d="M23 12v40" />
            <path className="bs-vault-glow-book-ring" stroke="url(#vaultGlowBookStroke)" d="M14 20h7M14 28h7M14 36h7M14 44h7" />
            <path className="bs-vault-glow-book-wave" stroke="url(#vaultGlowBookStroke)" d="M30 30c4-7 9 7 13 0" />
            <path className="bs-vault-glow-book-line" stroke="url(#vaultGlowBookStroke)" d="M30 39h14" />
        </svg>
    );
}

export default function Notes() {
    const location = useLocation();
    const requestedNoteId = String((location.state as { noteId?: string } | null)?.noteId || "");
    const restoredVaultRef = useRef<VaultSnapshot | null>(readVaultSnapshot());
    const restoredVault = restoredVaultRef.current;
    const isDesktopVaultLayout = useMediaQuery("(min-width: 768px)");
    const [notes, setNotes] = useState<Note[]>(() => restoredVault?.notes || []);
    const [folders, setFolders] = useState<FolderType[]>(() => restoredVault?.folders || []);
    const [deletedFolders, setDeletedFolders] = useState<FolderType[]>([]);
    const [unfiledCount, setUnfiledCount] = useState(() => restoredVault?.unfiledCount || 0);
    const [favouritesCount, setFavouritesCount] = useState(() => restoredVault?.favouritesCount || 0);
    const [sharedTotalCount, setSharedTotalCount] = useState(() => restoredVault?.sharedTotalCount || 0);
    const [totalNotes, setTotalNotes] = useState(() => restoredVault?.totalNotes || 0);
    const [hasMore, setHasMore] = useState(() => restoredVault?.hasMore || false);
    const [currentPage, setCurrentPage] = useState(() => restoredVault?.currentPage || 1);

    const [loading, setLoading] = useState(() => !restoredVault?.notes?.length);
    const [loadingMore, setLoadingMore] = useState(false);
    const [noteVisibleCount, setNoteVisibleCount] = useState(NOTE_BATCH_SIZE);
    const [folderVisibleCount, setFolderVisibleCount] = useState(FOLDER_BATCH_SIZE);
    const [deletedFolderVisibleCount, setDeletedFolderVisibleCount] = useState(FOLDER_BATCH_SIZE);
    const [showNoteSkeletons, setShowNoteSkeletons] = useState(() => !restoredVault?.notes?.length);
    const [searchQuery, setSearchQuery] = useState(() => restoredVault?.searchQuery || "");
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [mobileShowAllNotes, setMobileShowAllNotes] = useState(false);
    const [commandOpen, setCommandOpen] = useState(false);
    const [commandQuery, setCommandQuery] = useState("");
    const mobileSearchInputRef = useRef<HTMLInputElement | null>(null);
    const [activeFilter, setActiveFilter] = useState<SidebarFilter>(() => normalizeVisibleVaultFilter(restoredVault?.activeFilter));
    const [activeType, setActiveType] = useState<NoteTypeFilter>(() => restoredVault?.activeType || "all");
    const [viewMode, setViewMode] = useState<ViewMode>(() => restoredVault?.viewMode || (localStorage.getItem("vaultViewModeV2") as ViewMode) || "grid-small");
    const [contentView, setContentView] = useState<ContentView>(() => restoredVault?.contentView || "both");
    const [sortMode, setSortMode] = useState<SortMode>(() => restoredVault?.sortMode || "created");
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    useEffect(() => {
        if (!mobileSearchOpen) return;
        const timer = window.setTimeout(() => mobileSearchInputRef.current?.focus(), 40);
        return () => window.clearTimeout(timer);
    }, [mobileSearchOpen]);

    useEffect(() => {
        setMobileShowAllNotes(false);
    }, [activeFilter, searchQuery]);

    const [showFolderModal, setShowFolderModal] = useState(false);
    const [editingFolder, setEditingFolder] = useState<FolderType | null>(null);
    const [folderMenuOpen, setFolderMenuOpen] = useState<string | null>(null);
    const [folderName, setFolderName] = useState("");
    const [folderColor, setFolderColor] = useState("#a855f7");
    const [folderParent, setFolderParent] = useState<string | null>(null);
    const [foldersCollapsed, setFoldersCollapsed] = useState(false);
    const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({});
    const [folderSaving, setFolderSaving] = useState(false);
    const [selectedNoteIds, setSelectedNoteIds] = useState<Set<string>>(() => new Set());
    const [selectedFolderIds, setSelectedFolderIds] = useState<Set<string>>(() => new Set());
    const [draggedNoteId, setDraggedNoteId] = useState<string | null>(null);
    const [draggedFolderId, setDraggedFolderId] = useState<string | null>(null);
    const [moveNoteTarget, setMoveNoteTarget] = useState<Note | null>(null);
    const [bulkFolder, setBulkFolder] = useState<string>("");
    const [bulkTags, setBulkTags] = useState("");
    const [bulkShareUsername, setBulkShareUsername] = useState("");
    const [bulkActionSheet, setBulkActionSheet] = useState<VaultBulkActionSheet>(null);

    const [selectedNote, setSelectedNote] = useState<Note | null>(() => {
        const selectedNoteId = restoredVault?.selectedNoteId;
        return selectedNoteId ? restoredVault?.notes.find((note) => note.id === selectedNoteId) || null : null;
    });
    const [selectedNoteDirty, setSelectedNoteDirty] = useState(false);

    const [showNoteModal, setShowNoteModal] = useState(false);
    const [newTitle, setNewTitle] = useState("");
    const [newContent, setNewContent] = useState("");
    const [newTags, setNewTags] = useState("");
    const [newFolder, setNewFolder] = useState<string | null>(null);
    const [newType, setNewType] = useState<NoteType>("text");
    const [newColor, setNewColor] = useState("#22d3ee");
    const [noteSaving, setNoteSaving] = useState(false);

    const [pendingConfirm, setPendingConfirm] = useState<ConfirmState | null>(null);
    const [confirmBusy, setConfirmBusy] = useState(false);
    const confirmBusyRef = useRef(false);

    const selectedFolder = useMemo(
        () => folders.find((folder) => folder.id === activeFilter),
        [activeFilter, folders]
    );
    const vaultSearchPlaceholder = selectedFolder
        ? `Search inside ${selectedFolder.name}...`
        : "Search notes, tags, folders...";
    const notesRef = useRef(notes);
    const lastQueryKeyRef = useRef(restoredVault?.queryKey || "");
    const loadRequestRef = useRef(0);
    const vaultPreferencesLoadedRef = useRef(false);
    const vaultPreferencesHydratingRef = useRef(false);
    const handledRequestedNoteRef = useRef("");

    useEffect(() => {
        notesRef.current = notes;
    }, [notes]);

    useEffect(() => {
        if (!requestedNoteId || handledRequestedNoteRef.current === requestedNoteId) return;
        handledRequestedNoteRef.current = requestedNoteId;
        let alive = true;
        const cached = notesRef.current.find((note) => note.id === requestedNoteId);
        if (cached) {
            setSelectedNote(cached);
            setSelectedNoteDirty(false);
            return;
        }
        fetchNote(requestedNoteId)
            .then((note) => {
                if (!alive) return;
                setNotes((current) => current.some((item) => item.id === note.id) ? current : [note, ...current]);
                setSelectedNote(note);
                setSelectedNoteDirty(false);
            })
            .catch((err: unknown) => {
                if (!alive) return;
                setError(err instanceof Error ? err.message : "Could not open this note.");
            });
        return () => {
            alive = false;
        };
    }, [requestedNoteId]);

    useEffect(() => {
        writeVaultSnapshot({
            notes,
            folders,
            unfiledCount,
            favouritesCount,
            sharedTotalCount,
            totalNotes,
            hasMore,
            currentPage,
            searchQuery,
            activeFilter,
            activeType,
            viewMode,
            contentView,
            sortMode,
            queryKey: lastQueryKeyRef.current,
            scrollY: window.scrollY,
            selectedNoteId: selectedNote?.id || null,
            savedAt: Date.now(),
        });
    }, [activeFilter, activeType, contentView, currentPage, favouritesCount, folders, hasMore, notes, searchQuery, selectedNote?.id, sharedTotalCount, sortMode, totalNotes, unfiledCount, viewMode]);

    useEffect(() => {
        const restored = restoredVaultRef.current;
        if (!restored?.scrollY) return;
        const frame = window.requestAnimationFrame(() => {
            window.scrollTo({ top: restored.scrollY, behavior: "auto" });
        });
        return () => window.cancelAnimationFrame(frame);
    }, []);

    useEffect(() => {
        let alive = true;
        vaultPreferencesHydratingRef.current = true;
        fetchVaultPreferences()
            .then((preferences) => {
                if (!alive || !preferences) return;
                setActiveFilter(normalizeVisibleVaultFilter(preferences.activeFilter));
                setActiveType(preferences.activeType);
                setContentView(preferences.contentView);
                setViewMode(preferences.viewMode);
                setSortMode(preferences.sortMode);
            })
            .catch((err) => {
                console.error("Vault preferences load failed:", err);
            })
            .finally(() => {
                if (!alive) return;
                vaultPreferencesHydratingRef.current = false;
                vaultPreferencesLoadedRef.current = true;
            });
        return () => {
            alive = false;
        };
    }, []);

    const buildParams = useCallback(
        (page: number): FetchNotesParams => {
            const params: FetchNotesParams = { page, limit: PAGE_SIZE };
            if (searchQuery.trim()) params.search = searchQuery.trim();
            if (activeFilter === "favourites") params.pinned = true;
            else if (activeFilter === "shared") {
                params.shared = true;
                params.sharedScope = "all";
            }
            else if (activeFilter === "recycle-bin") params.trashed = true;
            else if (isFolderFilter(activeFilter)) params.folder = activeFilter;
            if (activeType !== "all") params.type = activeType;
            params.sort = sortMode;
            return params;
        },
        [activeFilter, activeType, searchQuery, sortMode]
    );
    const activeQueryKey = useMemo(() => vaultQueryKey(buildParams(1)), [buildParams]);

    const loadFolders = useCallback(async () => {
        try {
            const [active, trashed] = await Promise.all([
                fetchFolders(),
                fetchFolders({ trashed: true }),
            ]);
            setFolders(active.folders);
            setDeletedFolders(trashed.folders);
            setUnfiledCount(active.unfiledCount);
        } catch (err) {
            console.error("Folder load failed:", err);
        }
    }, []);

    const loadQuickCounts = useCallback(async () => {
        try {
            const [favourites, sharedAll] = await Promise.all([
                fetchNotes({ pinned: true, limit: 1 }),
                fetchNotes({ shared: true, sharedScope: "all", limit: 1 }),
            ]);
            setFavouritesCount(favourites.total);
            setSharedTotalCount(sharedAll.total);
        } catch (err) {
            console.error("Vault counts failed:", err);
        }
    }, []);

    const loadNotes = useCallback(async () => {
        const params = buildParams(1);
        const queryKey = vaultQueryKey(params);
        const cached = readVaultQueryCache(queryKey);

        if (cached) {
            setNotes(cached.notes);
            setTotalNotes(cached.totalNotes);
            setHasMore(cached.hasMore);
            setCurrentPage(cached.currentPage);
            setLoading(false);
        } else if (lastQueryKeyRef.current !== queryKey) {
            setNotes([]);
            setTotalNotes(0);
            setHasMore(false);
            setCurrentPage(1);
            setLoading(true);
        } else if (notesRef.current.length === 0) {
            setLoading(true);
        }

        lastQueryKeyRef.current = queryKey;
        setError("");
        const requestId = ++loadRequestRef.current;
        try {
            const res = await fetchNotes(params);
            if (requestId !== loadRequestRef.current) return;
            setNotes(res.notes);
            setTotalNotes(res.total);
            setHasMore(res.hasMore);
            setCurrentPage(1);
            writeVaultQueryCache(queryKey, {
                notes: res.notes,
                totalNotes: res.total,
                hasMore: res.hasMore,
                currentPage: 1,
            });
        } catch (err: unknown) {
            if (requestId !== loadRequestRef.current) return;
            const msg = err instanceof Error ? err.message : "Failed to load notes";
            setError(msg);
            if (!cached && notesRef.current.length === 0) {
                setNotes([]);
                setTotalNotes(0);
            }
        } finally {
            if (requestId === loadRequestRef.current) setLoading(false);
        }
    }, [buildParams]);

    useEffect(() => {
        loadFolders();
        loadQuickCounts();
    }, [loadFolders, loadQuickCounts]);

    useEffect(() => {
        localStorage.setItem("vaultViewModeV2", viewMode);
    }, [viewMode]);

    useEffect(() => {
        if (!vaultPreferencesLoadedRef.current || vaultPreferencesHydratingRef.current) return;
        const timer = window.setTimeout(() => {
            saveVaultPreferences({
                activeFilter,
                activeType,
                contentView,
                viewMode,
                sortMode,
            }).catch((err) => {
                console.error("Vault preferences save failed:", err);
            });
        }, 450);
        return () => window.clearTimeout(timer);
    }, [activeFilter, activeType, contentView, sortMode, viewMode]);

    useEffect(() => {
        setSelectedNoteIds(new Set());
        setSelectedFolderIds(new Set());
    }, [activeFilter, searchQuery]);

    useEffect(() => {
        if (activeFilter === "unfiled") setActiveFilter("all");
    }, [activeFilter]);

    useEffect(() => {
        setFolderVisibleCount(FOLDER_BATCH_SIZE);
        setDeletedFolderVisibleCount(FOLDER_BATCH_SIZE);
        setNoteVisibleCount(NOTE_BATCH_SIZE);
    }, [activeFilter, activeType, contentView, searchQuery, sortMode, viewMode]);

    useEffect(() => {
        if (!notice) return;
        const timer = window.setTimeout(() => setNotice(""), 2600);
        return () => window.clearTimeout(timer);
    }, [notice]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            loadNotes();
        }, searchQuery.trim() ? 250 : 0);
        return () => window.clearTimeout(timer);
    }, [loadNotes, searchQuery]);

    useEffect(() => {
        const handleEsc = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return;
            if (commandOpen) setCommandOpen(false);
            else if (pendingConfirm) setPendingConfirm(null);
            else if (folderMenuOpen) setFolderMenuOpen(null);
            else if (selectedNote) {
                if (selectedNoteDirty) {
                    setPendingConfirm({
                        title: "Close note?",
                        message: "This note has unsaved edits. Closing it will discard the draft.",
                        confirmLabel: "Close note",
                        tone: "danger",
                        onConfirm: () => {
                            setSelectedNote(null);
                            setSelectedNoteDirty(false);
                        },
                    });
                    return;
                }
                setSelectedNote(null);
                setSelectedNoteDirty(false);
            }
            else if (showNoteModal) {
                if (newTitle.trim() || newContent.trim() || newTags.trim()) {
                    setPendingConfirm({
                        title: "Discard new note?",
                        message: "Your unsaved title, tags, and writing will be removed.",
                        confirmLabel: "Discard note",
                        tone: "danger",
                        onConfirm: () => {
                            clearNewNoteDraft();
                            setShowNoteModal(false);
                        },
                    });
                } else setShowNoteModal(false);
            }
            else if (showFolderModal) {
                const isFolderDirty = editingFolder
                    ? folderName !== editingFolder.name ||
                        folderColor !== (editingFolder.color || "#a855f7") ||
                        folderParent !== (editingFolder.parent || null)
                    : Boolean(folderName.trim());
                if (isFolderDirty) {
                    setPendingConfirm({
                        title: "Discard folder changes?",
                        message: "The folder name, parent, or colour changes have not been saved.",
                        confirmLabel: "Discard changes",
                        tone: "danger",
                        onConfirm: () => setShowFolderModal(false),
                    });
                } else setShowFolderModal(false);
            }
        };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, [commandOpen, editingFolder, folderColor, folderMenuOpen, folderName, folderParent, newContent, newTags, newTitle, pendingConfirm, selectedNote, selectedNoteDirty, showFolderModal, showNoteModal]);

    useEffect(() => {
        if (!folderMenuOpen) return;
        const close = () => setFolderMenuOpen(null);
        document.addEventListener("pointerdown", close);
        return () => document.removeEventListener("pointerdown", close);
    }, [folderMenuOpen]);

    const loadMore = async () => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        try {
            const nextPage = currentPage + 1;
            const params = buildParams(nextPage);
            const queryKey = vaultQueryKey(params);
            const res = await fetchNotes(params);
            setNotes((prev) => {
                const merged = [...prev, ...res.notes];
                writeVaultQueryCache(queryKey, {
                    notes: merged,
                    totalNotes: res.total,
                    hasMore: res.hasMore,
                    currentPage: nextPage,
                });
                return merged;
            });
            setNoteVisibleCount((count) => count + Math.max(NOTE_BATCH_SIZE, res.notes.length));
            setHasMore(res.hasMore);
            setCurrentPage(nextPage);
        } catch (err) {
            console.error("Load more notes failed:", err);
        } finally {
            setLoadingMore(false);
        }
    };

    const openNewNote = (folderId?: string | null, type: NoteType = "text") => {
        const draft = readNewNoteDraft();
        setNewType(draft?.type || type);
        setNewFolder(draft?.folder ?? folderId ?? (isFolderFilter(activeFilter) ? activeFilter : null));
        setNewTitle(draft?.title || "");
        setNewContent(draft?.content || "");
        setNewTags(draft?.tags || "");
        setNewColor(draft?.color || "#22d3ee");
        setShowNoteModal(true);
    };

    const newNoteDirty = Boolean(newTitle.trim() || newContent.trim() || newTags.trim());
    const folderDirty = editingFolder
        ? folderName !== editingFolder.name ||
            folderColor !== (editingFolder.color || "#a855f7") ||
            folderParent !== (editingFolder.parent || null)
        : Boolean(folderName.trim());

    const requestConfirm = (state: ConfirmState) => {
        setPendingConfirm(state);
    };

    const requestCloseNoteModal = () => {
        if (noteSaving) return;
        if (!newNoteDirty) {
            setShowNoteModal(false);
            return;
        }
        requestConfirm({
            title: "Discard new note?",
            message: "Your unsaved title, tags, and writing will be removed.",
            confirmLabel: "Discard note",
            tone: "danger",
            onConfirm: () => {
                clearNewNoteDraft();
                setShowNoteModal(false);
            },
        });
    };

    const requestCloseFolderModal = () => {
        if (folderSaving) return;
        if (!folderDirty) {
            setShowFolderModal(false);
            return;
        }
        requestConfirm({
            title: "Discard folder changes?",
            message: "The folder name, parent, or colour changes have not been saved.",
            confirmLabel: "Discard changes",
            tone: "danger",
            onConfirm: () => setShowFolderModal(false),
        });
    };

    useEffect(() => {
        if (!showNoteModal || !newNoteDirty) return;
        const timer = window.setTimeout(() => {
            writeNewNoteDraft({
                title: newTitle,
                content: newContent,
                tags: newTags,
                folder: newFolder,
                type: newType,
                color: newColor,
            });
        }, 300);
        return () => window.clearTimeout(timer);
    }, [newColor, newContent, newFolder, newNoteDirty, newTags, newTitle, newType, showNoteModal]);

    const openCreateFolder = (parentId: string | null = isFolderFilter(activeFilter) ? activeFilter : null) => {
        setEditingFolder(null);
        setFolderName("");
        setFolderColor("#a855f7");
        setFolderParent(parentId);
        setShowFolderModal(true);
    };

    const openEditFolder = (folder: FolderType) => {
        setFolderMenuOpen(null);
        setEditingFolder(folder);
        setFolderName(folder.name);
        setFolderColor(folder.color || "#a855f7");
        setFolderParent(folder.parent || null);
        setShowFolderModal(true);
    };

    const openNote = (note: Note) => {
        if (selectedNote && selectedNote.id !== note.id && selectedNoteDirty) {
            requestConfirm({
                title: "Switch notes?",
                message: "The open note has unsaved edits. Switching will discard those edits.",
                confirmLabel: "Switch note",
                tone: "danger",
                onConfirm: () => {
                    setSelectedNote(note);
                    setSelectedNoteDirty(false);
                },
            });
            return;
        }
        setSelectedNote(note);
        setSelectedNoteDirty(false);
    };

    const closeOpenNote = () => {
        if (selectedNoteDirty) {
            requestConfirm({
                title: "Close note?",
                message: "This note has unsaved edits. Closing it will discard the draft.",
                confirmLabel: "Close note",
                tone: "danger",
                onConfirm: () => {
                    setSelectedNote(null);
                    setSelectedNoteDirty(false);
                },
            });
            return;
        }
        setSelectedNote(null);
        setSelectedNoteDirty(false);
    };

    const updateOpenNote = (updated: Note) => {
        setSelectedNote((prev) => (prev?.id === updated.id ? updated : prev));
        setNotes((prev) => prev.map((note) => (note.id === updated.id ? updated : note)));
        setSelectedNoteDirty(false);
    };

    const handleCreateNote = async () => {
        if (!newTitle.trim() || noteSaving) return;
        setNoteSaving(true);
        setError("");
        try {
            const input: CreateNoteInput = {
                title: newTitle.trim(),
                content: newContent.trim(),
                type: newType,
                color: newColor,
                tags: newTags
                    .split(",")
                    .map((tag) => tag.trim())
                    .filter(Boolean),
                folder: newFolder,
            };
            await createNote(input);
            clearNewNoteDraft();
            setShowNoteModal(false);
            await Promise.all([loadNotes(), loadFolders(), loadQuickCounts()]);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to create note");
        } finally {
            setNoteSaving(false);
        }
    };

    const handleSaveFolder = async () => {
        if (!folderName.trim() || folderSaving) return;
        setFolderSaving(true);
        setError("");
        try {
            if (editingFolder) {
                await updateFolder(editingFolder.id, {
                    name: folderName.trim(),
                    color: folderColor,
                    parent: folderParent,
                });
            } else {
                await apiCreateFolder(folderName.trim(), folderColor, folderParent);
            }
            setShowFolderModal(false);
            setEditingFolder(null);
            await loadFolders();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to save folder");
        } finally {
            setFolderSaving(false);
        }
    };

    const afterFolderMutation = async (options: { refreshCounts?: boolean } = {}) => {
        await Promise.all([
            loadFolders(),
            loadNotes(),
            ...(options.refreshCounts ? [loadQuickCounts()] : []),
        ]);
    };

    const handleToggleFolderPin = async (folder: FolderType) => {
        setFolderMenuOpen(null);
        try {
            await updateFolder(folder.id, { isPinned: !folder.isPinned });
            await loadFolders();
            setNotice(folder.isPinned ? "Folder unpinned" : "Folder pinned");
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to update folder");
        }
    };

    const handleDeleteFolder = (folder: FolderType) => {
        setFolderMenuOpen(null);
        const childCount = folder.childFolderCount ?? folders.filter((item) => item.parent === folder.id).length;
        const hasContents = (folder.noteCount || 0) > 0 || childCount > 0;
        setPendingConfirm({
            title: "Move folder to Recycle Bin?",
            message: hasContents
                ? `"${folder.name}" and its notes will move to Recycle Bin.`
                : `"${folder.name}" will move to Recycle Bin.`,
            confirmLabel: "Move to Recycle Bin",
            tone: "danger",
            onConfirm: async () => {
                await deleteFolder(folder.id);
                if (activeFilter === folder.id) setActiveFilter("all");
                await afterFolderMutation();
                setNotice(hasContents ? "Folder and notes moved to Recycle Bin" : "Folder moved to Recycle Bin");
            },
        });
    };

    const handleTogglePin = async (event: React.MouseEvent, noteId: string) => {
        event.stopPropagation();
        try {
            const isPinned = await togglePin(noteId);
            setNotes((prev) => prev.map((note) => (note.id === noteId ? { ...note, isPinned } : note)));
            setSelectedNote((prev) => (prev?.id === noteId ? { ...prev, isPinned } : prev));
            loadQuickCounts();
        } catch (err) {
            console.error("Pin toggle failed:", err);
        }
    };

    const removeNoteFromView = (noteId: string) => {
        setNotes((prev) => prev.filter((note) => note.id !== noteId));
        setTotalNotes((prev) => Math.max(0, prev - 1));
        setSelectedNote((prev) => (prev?.id === noteId ? null : prev));
        setSelectedNoteDirty(false);
    };

    const handleTrashNote = (event: React.MouseEvent, note: Note) => {
        event.stopPropagation();

        if (activeFilter === "recycle-bin") {
            setPendingConfirm({
                title: "Delete forever?",
                message: `"${note.title}" will be permanently deleted. This cannot be undone.`,
                confirmLabel: "Delete forever",
                tone: "danger",
                onConfirm: async () => {
                    await deleteNotePermanently(note.id);
                    removeNoteFromView(note.id);
                    await Promise.all([loadFolders(), loadQuickCounts()]);
                    setNotice("Note deleted forever");
                },
            });
            return;
        }

        toggleTrash(note.id)
            .then(() => {
                removeNoteFromView(note.id);
                loadFolders();
                loadQuickCounts();
                setNotice("Note moved to Recycle Bin");
            })
            .catch((err) => console.error("Trash note failed:", err));
    };

    const handleRestoreFromTrash = async (event: React.MouseEvent, noteId: string) => {
        event.stopPropagation();
        try {
            await toggleTrash(noteId);
            removeNoteFromView(noteId);
            await Promise.all([loadFolders(), loadQuickCounts()]);
            setNotice("Note restored");
        } catch (err) {
            console.error("Restore note failed:", err);
        }
    };

    const toggleNoteSelection = (note: Note) => {
        setSelectedNoteIds((prev) => {
            const next = new Set(prev);
            if (next.has(note.id)) next.delete(note.id);
            else next.add(note.id);
            return next;
        });
    };

    const toggleFolderSelection = (folder: FolderType) => {
        setSelectedFolderIds((prev) => {
            const next = new Set(prev);
            if (next.has(folder.id)) next.delete(folder.id);
            else next.add(folder.id);
            return next;
        });
    };

    const mobileSelectionPressTimerRef = useRef<number | null>(null);
    const mobileSelectionPressTriggeredRef = useRef(false);

    const clearMobileSelectionPress = () => {
        if (mobileSelectionPressTimerRef.current) window.clearTimeout(mobileSelectionPressTimerRef.current);
        mobileSelectionPressTimerRef.current = null;
    };

    const beginMobileSelectionPress = (select: () => void) => {
        mobileSelectionPressTriggeredRef.current = false;
        clearMobileSelectionPress();
        mobileSelectionPressTimerRef.current = window.setTimeout(() => {
            mobileSelectionPressTriggeredRef.current = true;
            select();
        }, 430);
    };

    const consumeMobileSelectionPress = (event: React.MouseEvent) => {
        if (!mobileSelectionPressTriggeredRef.current) return false;
        event.preventDefault();
        mobileSelectionPressTriggeredRef.current = false;
        return true;
    };

    const clearSelection = () => {
        setSelectedNoteIds(new Set());
        setSelectedFolderIds(new Set());
        setBulkFolder("");
        setBulkTags("");
        setBulkShareUsername("");
        setBulkActionSheet(null);
    };

    const moveNoteToFolder = async (noteId: string, folderId: string | null) => {
        const updated = await updateNote(noteId, { folder: folderId });
        setNotes((prev) => prev.map((note) => (note.id === noteId ? updated : note)));
        setSelectedNote((prev) => (prev?.id === noteId ? updated : prev));
    };

    const handleMoveNoteRequest = (event: React.MouseEvent, note: Note) => {
        event.stopPropagation();
        setMoveNoteTarget(note);
    };

    const handleDropNoteIntoFolder = async (folderId: string | null) => {
        if (!draggedNoteId) return;
        try {
            await moveNoteToFolder(draggedNoteId, folderId);
            setDraggedNoteId(null);
            await loadFolders();
            setNotice(folderId ? "Note moved to folder" : "Note removed from folder");
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to move note");
        }
    };

    const handleDropFolderIntoFolder = async (parentId: string | null) => {
        if (!draggedFolderId) return;
        const draggedFolder = folders.find((folder) => folder.id === draggedFolderId);
        if (!draggedFolder) {
            setDraggedFolderId(null);
            return;
        }
        const invalidTargets = descendantFolderIds(folders, draggedFolder.id);
        if (parentId === draggedFolder.id || (parentId && invalidTargets.has(parentId))) {
            setDraggedFolderId(null);
            setError("A folder cannot be moved inside itself or one of its subfolders.");
            return;
        }
        try {
            await updateFolder(draggedFolder.id, { parent: parentId });
            setDraggedFolderId(null);
            await loadFolders();
            setNotice(parentId ? "Folder moved" : "Folder moved to Vault root");
        } catch (err: unknown) {
            setDraggedFolderId(null);
            setError(err instanceof Error ? err.message : "Failed to move folder");
        }
    };

    const handleBulkMove = async () => {
        const noteIds = Array.from(selectedNoteIds);
        const folderIds = Array.from(selectedFolderIds);
        if (noteIds.length === 0 && folderIds.length === 0) return;
        try {
            const folderId = bulkFolder || null;
            await bulkMoveVaultItems({ noteIds, folderIds, folder: folderId });
            await Promise.all([loadFolders(), loadNotes()]);
            clearSelection();
            setNotice(`Moved ${noteIds.length + folderIds.length} item${noteIds.length + folderIds.length === 1 ? "" : "s"}`);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to move selected items");
        }
    };

    const handleBulkTrash = async () => {
        const noteIds = Array.from(selectedNoteIds);
        const folderIds = Array.from(selectedFolderIds);
        const total = noteIds.length + folderIds.length;
        if (total === 0) return;
        setPendingConfirm({
            title: "Move selected items?",
            message: folderIds.length > 0
                ? `${total} selected item${total === 1 ? "" : "s"} will move to Recycle Bin. Folder notes move with their folders.`
                : `${total} selected item${total === 1 ? "" : "s"} will move to Recycle Bin.`,
            confirmLabel: "Move selected",
            tone: "danger",
            onConfirm: async () => {
                await bulkTrashVaultItems({ noteIds, folderIds });
                setNotes((prev) => prev.filter((note) => !selectedNoteIds.has(note.id)));
                setTotalNotes((prev) => Math.max(0, prev - noteIds.length));
                clearSelection();
                await Promise.all([loadFolders(), loadNotes(), loadQuickCounts()]);
                setNotice(folderIds.length > 0 ? "Selected folders and notes moved to Recycle Bin" : total === 1 ? "Moved to Recycle Bin" : "Selected items moved to Recycle Bin");
            },
        });
    };

    const handleBulkRestoreFromTrash = async () => {
        const noteIds = Array.from(selectedNoteIds);
        const folderIds = Array.from(selectedFolderIds);
        const total = noteIds.length + folderIds.length;
        if (total === 0) return;
        try {
            await bulkRestoreVaultItems({ noteIds, folderIds });
            setNotes((prev) => prev.filter((note) => !selectedNoteIds.has(note.id)));
            setDeletedFolders((prev) => prev.filter((folder) => !selectedFolderIds.has(folder.id)));
            clearSelection();
            await Promise.all([loadFolders(), loadNotes(), loadQuickCounts()]);
            setNotice(total === 1 ? "Item restored" : "Selected items restored");
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to restore selected items");
        }
    };

    const handleBulkDeleteForever = async () => {
        const noteIds = Array.from(selectedNoteIds);
        const folderIds = Array.from(selectedFolderIds);
        const total = noteIds.length + folderIds.length;
        if (total === 0) return;
        setPendingConfirm({
            title: "Delete selected forever?",
            message: folderIds.length > 0
                ? `${total} selected item${total === 1 ? "" : "s"} will be permanently removed. Notes inside selected folders will be deleted too.`
                : `${total} selected item${total === 1 ? "" : "s"} will be permanently removed. This cannot be undone.`,
            confirmLabel: "Delete forever",
            tone: "danger",
            onConfirm: async () => {
                await bulkDeleteVaultItems({ noteIds, folderIds });
                setNotes((prev) => prev.filter((note) => !selectedNoteIds.has(note.id)));
                setDeletedFolders((prev) => prev.filter((folder) => !selectedFolderIds.has(folder.id)));
                setTotalNotes((prev) => Math.max(0, prev - noteIds.length));
                clearSelection();
                await Promise.all([loadFolders(), loadNotes(), loadQuickCounts()]);
                setNotice(folderIds.length > 0 ? "Selected folders and notes deleted forever" : total === 1 ? "Item deleted forever" : "Selected items deleted forever");
            },
        });
    };

    const handleEmptyRecycleBin = async () => {
        setPendingConfirm({
            title: "Empty Recycle Bin?",
            message: "All deleted notes and folders in your bin will be permanently removed. This cannot be undone.",
            confirmLabel: "Empty bin",
            tone: "danger",
            onConfirm: async () => {
                await bulkDeleteVaultItems({ emptyTrash: true });
                setNotes([]);
                setDeletedFolders([]);
                setTotalNotes(0);
                setHasMore(false);
                clearSelection();
                await Promise.all([loadFolders(), loadNotes(), loadQuickCounts()]);
                setNotice("Recycle Bin emptied");
            },
        });
    };

    const handleBulkPin = async () => {
        const selected = notes.filter((note) => selectedNoteIds.has(note.id) && !note.isPinned);
        const selectedFolderItems = folders.filter((folder) => selectedFolderIds.has(folder.id) && !folder.isPinned);
        if (selected.length === 0 && selectedFolderItems.length === 0) return;
        try {
            await bulkPinVaultItems({
                noteIds: selected.map((note) => note.id),
                folderIds: selectedFolderItems.map((folder) => folder.id),
                isPinned: true,
            });
            setNotes((prev) => prev.map((note) => selectedNoteIds.has(note.id) ? { ...note, isPinned: true } : note));
            setFolders((prev) => prev.map((folder) => selectedFolderIds.has(folder.id) ? { ...folder, isPinned: true } : folder));
            clearSelection();
            await Promise.all([loadFolders(), loadQuickCounts()]);
            const total = selected.length + selectedFolderItems.length;
            setNotice(`Pinned ${total} item${total === 1 ? "" : "s"}`);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to star selected items");
        }
    };

    const handleBulkTag = async () => {
        const tags = bulkTags.split(",").map((tag) => tag.trim()).filter(Boolean);
        const ids = Array.from(selectedNoteIds);
        if (ids.length === 0 || tags.length === 0) return;
        try {
            const updated = await bulkTagNotes(ids, tags);
            const updatedMap = new Map(updated.map((note) => [note.id, note]));
            setNotes((prev) => prev.map((note) => updatedMap.get(note.id) || note));
            clearSelection();
            setNotice(`Tagged ${ids.length} note${ids.length === 1 ? "" : "s"}`);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to tag selected notes");
        }
    };

    const handleBulkShare = async () => {
        const username = normalizeUsernameSearch(bulkShareUsername);
        const selected = notes.filter((note) => selectedNoteIds.has(note.id) && note.isOwner !== false);
        const selectedFolderCount = selectedFolderIds.size;
        if (!username) return;
        if (selected.length === 0) {
            setError(selectedFolderCount > 0 ? "Folder sharing is not available yet. Select notes to share access." : "Select at least one note to share.");
            return;
        }
        try {
            const updated = await bulkShareNotes(selected.map((note) => note.id), username, "view");
            const updatedMap = new Map(updated.map((note) => [note.id, note]));
            setNotes((prev) => prev.map((note) => updatedMap.get(note.id) || note));
            clearSelection();
            await loadQuickCounts();
            setNotice(`Access granted: @${username} can view ${selected.length} note${selected.length === 1 ? "" : "s"}${selectedFolderCount > 0 ? ". Folder sharing is not available yet." : "."}`);
        } catch (err: unknown) {
            setError(err instanceof Error ? `Could not share selected notes: ${err.message}` : "Could not share selected notes.");
        }
    };

    const handleBulkCopy = async () => {
        const noteIds = Array.from(selectedNoteIds);
        const folderIds = Array.from(selectedFolderIds);
        if (noteIds.length === 0 && folderIds.length === 0) return;
        try {
            const result = await bulkCopyVaultItems({ noteIds, folderIds });
            clearSelection();
            await Promise.all([loadFolders(), loadNotes(), loadQuickCounts()]);
            const copiedTotal = result.copiedNotes + result.copiedFolders;
            if (result.copiedFolders > 0 && result.copiedNotes > 0) {
                setNotice(`${copiedTotal} items copied`);
            } else if (result.copiedFolders > 0) {
                setNotice(result.copiedFolders === 1 ? "Folder copied with contents" : `${result.copiedFolders} folders copied`);
            } else {
                setNotice(result.copiedNotes === 1 ? "Note copied" : `${result.copiedNotes} notes copied`);
            }
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to copy selected items");
        }
    };

    const handleShowMoreNotes = async () => {
        if (hiddenLoadedNoteCount > 0) {
            setNoteVisibleCount((count) => Math.min(visibleNotes.length, count + NOTE_BATCH_SIZE));
            return;
        }
        if (hasMore) await loadMore();
    };

    const runConfirm = async () => {
        if (!pendingConfirm || confirmBusyRef.current) return;
        confirmBusyRef.current = true;
        setConfirmBusy(true);
        setError("");
        try {
            await pendingConfirm.onConfirm();
            setPendingConfirm(null);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Action failed");
        } finally {
            confirmBusyRef.current = false;
            setConfirmBusy(false);
        }
    };

    const runSecondaryConfirm = async () => {
        if (!pendingConfirm?.onSecondary || confirmBusyRef.current) return;
        confirmBusyRef.current = true;
        setConfirmBusy(true);
        setError("");
        try {
            await pendingConfirm.onSecondary();
            setPendingConfirm(null);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Action failed");
        } finally {
            confirmBusyRef.current = false;
            setConfirmBusy(false);
        }
    };

    const openNoteSlots = useMemo(() => {
        const map = new Map<string, { index: number; color: string; active: boolean }>();
        if (selectedNote) {
            map.set(selectedNote.id, {
                index: 1,
                color: selectedNote.color || OPEN_COLORS[0],
                active: true,
            });
        }
        return map;
    }, [selectedNote]);
    const foldersByParent = useMemo(() => {
        const map = new Map<string, FolderType[]>();
        folders.forEach((folder) => {
            const key = folder.parent || "root";
            map.set(key, [...(map.get(key) || []), folder]);
        });
        map.forEach((items, key) => {
            map.set(key, items.sort((a, b) => Number(Boolean(b.isPinned)) - Number(Boolean(a.isPinned)) || a.name.localeCompare(b.name)));
        });
        return map;
    }, [folders]);
    const blockedFolderParentIds = useMemo(() => {
        if (!editingFolder) return new Set<string>();
        return new Set([editingFolder.id, ...Array.from(descendantFolderIds(folders, editingFolder.id))]);
    }, [editingFolder, folders]);

    const childFoldersInView = useMemo(() => {
        if (!isFolderFilter(activeFilter)) return [];
        return foldersByParent.get(activeFilter) || [];
    }, [activeFilter, foldersByParent]);

    const foldersInView = useMemo(() => {
        if (searchQuery.trim()) {
            const query = searchQuery.trim().toLowerCase();
            const searchPool = isFolderFilter(activeFilter) ? childFoldersInView : folders;
            return searchPool.filter((folder) => folder.name.toLowerCase().includes(query));
        }
        if (activeFilter === "all") return foldersByParent.get("root") || [];
        if (isFolderFilter(activeFilter)) return childFoldersInView;
        return [];
    }, [activeFilter, childFoldersInView, folders, foldersByParent, searchQuery]);
    const visibleFoldersInView = useMemo(
        () => foldersInView.slice(0, folderVisibleCount),
        [folderVisibleCount, foldersInView]
    );
    const hiddenFolderCount = Math.max(0, foldersInView.length - visibleFoldersInView.length);
    const visibleDeletedFolders = useMemo(
        () => deletedFolders.slice(0, deletedFolderVisibleCount),
        [deletedFolderVisibleCount, deletedFolders]
    );
    const hiddenDeletedFolderCount = Math.max(0, deletedFolders.length - visibleDeletedFolders.length);

    const inFolderView = isFolderFilter(activeFilter);
    const effectiveContentView = contentView;
    const showFolderContent = inFolderView || effectiveContentView !== "notes";
    const showNoteContent = inFolderView || effectiveContentView !== "folders";

    const notesMatchActiveQuery = lastQueryKeyRef.current === activeQueryKey;
    const notesTransitioning = !notesMatchActiveQuery;
    const notesLoadingVisible = notesTransitioning ? showNoteSkeletons : loading;
    const visibleNotes = useMemo(() => {
        return notesMatchActiveQuery ? notes : [];
    }, [notes, notesMatchActiveQuery]);
    const visibleNotesForGrid = useMemo(
        () => visibleNotes.slice(0, noteVisibleCount),
        [noteVisibleCount, visibleNotes]
    );
    const hiddenLoadedNoteCount = Math.max(0, visibleNotes.length - visibleNotesForGrid.length);
    const remainingNoteCount = Math.max(0, totalNotes - visibleNotesForGrid.length);
    const isRecycleBin = activeFilter === "recycle-bin";
    const recycleItemCount = isRecycleBin ? visibleNotes.length + deletedFolders.length : 0;

    useEffect(() => {
        if (!notesTransitioning) {
            setShowNoteSkeletons(false);
            return;
        }
        const timer = window.setTimeout(() => setShowNoteSkeletons(true), 140);
        return () => window.clearTimeout(timer);
    }, [notesTransitioning]);

    const selectedNotes = useMemo(
        () => visibleNotes.filter((note) => selectedNoteIds.has(note.id)),
        [selectedNoteIds, visibleNotes]
    );
    const selectedFolders = useMemo(
        () => (isRecycleBin ? deletedFolders : folders).filter((folder) => selectedFolderIds.has(folder.id)),
        [deletedFolders, folders, isRecycleBin, selectedFolderIds]
    );
    const selectedItemCount = selectedNotes.length + selectedFolders.length;
    const selectionMode = selectedItemCount > 0;
    const handleEditSelectedItem = () => {
        if (selectedItemCount !== 1) return;
        if (selectedNotes.length === 1) {
            const note = selectedNotes[0];
            clearSelection();
            openNote(note);
            return;
        }
        if (selectedFolders.length === 1) {
            const folder = selectedFolders[0];
            clearSelection();
            openEditFolder(folder);
        }
    };
    const selectionAccent = useMemo(
        () => blendHexColors([
            ...selectedNotes.map((note) => note.color),
            ...selectedFolders.map((folder) => folder.color),
        ]),
        [selectedFolders, selectedNotes]
    );
    const selectionAccentStyle = {
        "--selection-accent": selectionAccent,
    } as React.CSSProperties;
    const recycleHeaderActions = isRecycleBin && recycleItemCount > 0 ? (
        <div className="inline-flex shrink-0 items-center gap-2">
            {selectedItemCount > 0 && (
                <button
                    type="button"
                    onClick={handleBulkRestoreFromTrash}
                    className="grid h-8 w-8 place-items-center rounded-lg bg-black/35 text-emerald-200 transition-colors hover:bg-emerald-950/30"
                    title="Restore selected"
                    aria-label="Restore selected"
                >
                    <RotateCcw size={14} />
                </button>
            )}
            <button
                type="button"
                onClick={selectedItemCount > 0 ? handleBulkDeleteForever : handleEmptyRecycleBin}
                className="grid h-8 w-8 place-items-center rounded-lg bg-black/35 text-rose-200 transition-colors hover:bg-rose-950/30"
                title={selectedItemCount > 0 ? "Delete selected forever" : "Empty bin"}
                aria-label={selectedItemCount > 0 ? "Delete selected forever" : "Empty bin"}
            >
                <Trash2 size={14} />
            </button>
        </div>
    ) : null;

    const topTags = useMemo(() => {
        const counts = new Map<string, number>();
        visibleNotes.forEach((note) => {
            note.tags.forEach((tag) => counts.set(tag, (counts.get(tag) || 0) + 1));
        });
        return Array.from(counts.entries())
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
            .slice(0, 10);
    }, [visibleNotes]);

    const folderPath = useMemo(() => {
        if (!selectedFolder) return [];
        const byId = new Map(folders.map((folder) => [folder.id, folder]));
        const path: FolderType[] = [];
        let current: FolderType | undefined = selectedFolder;
        const seen = new Set<string>();
        while (current && !seen.has(current.id)) {
            path.unshift(current);
            seen.add(current.id);
            current = current.parent ? byId.get(current.parent) : undefined;
        }
        return path;
    }, [folders, selectedFolder]);

    const smartFolderSuggestions = useMemo(() => {
        const haystack = `${newTitle} ${newTags} ${notePreview(newContent)}`.toLowerCase();
        if (!haystack.trim()) return [];
        return folders
            .map((folder) => {
                const folderWords = folder.name.toLowerCase().split(/\s+/).filter(Boolean);
                const score = folderWords.reduce((total, word) => total + (haystack.includes(word) ? 2 : 0), 0)
                    + (folder.noteCount > 0 ? 0.2 : 0)
                    + (folder.isPinned ? 0.5 : 0);
                return { folder, score };
            })
            .filter((item) => item.score > 0)
            .sort((a, b) => b.score - a.score || a.folder.name.localeCompare(b.folder.name))
            .slice(0, 3)
            .map((item) => item.folder);
    }, [folders, newContent, newTags, newTitle]);

    const commandMatches = useMemo(() => {
        const query = commandQuery.trim().toLowerCase();
        const noteMatches = visibleNotes
            .filter((note) => !query || note.title.toLowerCase().includes(query) || note.tags.some((tag) => tag.toLowerCase().includes(query)))
            .slice(0, 6);
        const folderMatches = folders
            .filter((folder) => !query || folder.name.toLowerCase().includes(query))
            .slice(0, 6);
        const tagMatches = topTags
            .filter(([tag]) => !query || tag.toLowerCase().includes(query))
            .slice(0, 6);
        return { noteMatches, folderMatches, tagMatches };
    }, [commandQuery, folders, topTags, visibleNotes]);

    const sharedWithMeNotes = useMemo(
        () => visibleNotes.filter((note) => note.isOwner === false),
        [visibleNotes]
    );

    const sharedByMeNotes = useMemo(
        () => visibleNotes.filter((note) => note.isOwner !== false && (note.sharedWith?.length || 0) > 0),
        [visibleNotes]
    );
    const sharedWithDisplayCount = sharedWithMeNotes.length;
    const sharedByDisplayCount = sharedByMeNotes.length;

    const noteGridClass = viewMode === "list" || viewMode === "simple" || (selectedNote && !isDesktopVaultLayout)
        ? "grid grid-cols-1 gap-3"
        : viewMode === "grid-small"
            ? "grid grid-cols-6 gap-x-2 gap-y-1 sm:grid-cols-8 md:grid-cols-11 2xl:grid-cols-12"
            : "grid grid-cols-5 gap-x-1 gap-y-2 sm:grid-cols-7 md:grid-cols-10 2xl:grid-cols-12";

    const showMobileVaultSummary = !isDesktopVaultLayout && !inFolderView && activeFilter !== "shared" && activeFilter !== "recycle-bin";
    const mobileFolderPreview = foldersInView.slice(0, 2);
    const mobileNotesPreview = mobileShowAllNotes ? visibleNotes : visibleNotes.slice(0, 3);

    const renderFolder = (folder: FolderType, depth = 0): React.ReactNode => {
        const children = foldersByParent.get(folder.id) || [];
        const expanded = expandedFolders[folder.id] === true;
        return (
            <div key={folder.id} className="relative">
                <div className="group relative" style={{ paddingLeft: `${depth * 14}px` }}>
                    <div
                        draggable
                        className={`flex items-center rounded-md transition-colors ${
                        activeFilter === folder.id
                            ? "bg-neutral-900 text-white"
                            : "text-neutral-400 hover:bg-neutral-900 hover:text-neutral-100"
                    }`}
                        onDragStart={(event) => {
                            event.dataTransfer.effectAllowed = "move";
                            setDraggedFolderId(folder.id);
                            setDraggedNoteId(null);
                        }}
                        onDragEnd={() => setDraggedFolderId(null)}
                        onDragOver={(event) => {
                            if (!draggedNoteId && !draggedFolderId) return;
                            event.preventDefault();
                        }}
                        onDrop={(event) => {
                            if (!draggedNoteId && !draggedFolderId) return;
                            event.preventDefault();
                            event.stopPropagation();
                            if (draggedNoteId) handleDropNoteIntoFolder(folder.id);
                            else handleDropFolderIntoFolder(folder.id);
                        }}
                    >
                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                if (children.length > 0) {
                                    setExpandedFolders((prev) => ({ ...prev, [folder.id]: !expanded }));
                                }
                            }}
                            className={`ml-1 flex h-7 w-7 shrink-0 items-center justify-center rounded text-neutral-600 transition-colors ${
                                children.length > 0 ? "hover:bg-neutral-800 hover:text-neutral-200" : "opacity-30"
                            }`}
                            title={children.length > 0 ? (expanded ? "Collapse folder" : "Expand folder") : "No subfolders"}
                        >
                            {children.length > 0 ? (expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span className="h-1 w-1 rounded-full bg-neutral-700" />}
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveFilter(folder.id)}
                            className="flex min-w-0 flex-1 items-center gap-2 py-2 pr-11 text-left text-sm"
                        >
                            <FolderIcon size={16} style={{ color: folder.color }} />
                            {folder.isPinned && <Pin size={11} className="shrink-0 fill-amber-300 text-amber-300" />}
                            <span className="min-w-0 flex-1 truncate">{folder.name}</span>
                            <span className="text-xs text-neutral-600">{folder.noteCount}</span>
                        </button>
                    </div>
                    <button
                        type="button"
                        onPointerDown={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                        onTouchStart={(event) => event.stopPropagation()}
                        onClick={(event) => {
                            event.stopPropagation();
                            setFolderMenuOpen(folderMenuOpen === folder.id ? null : folder.id);
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-neutral-600 opacity-100 transition-all hover:bg-neutral-800 hover:text-white sm:opacity-0 sm:group-hover:opacity-100"
                        title="Folder options"
                    >
                        <MoreHorizontal size={15} />
                    </button>
                    {folderMenuOpen === folder.id && (
                        <div
                            className="bs-vault-context-menu absolute right-2 top-9 z-20 w-52 rounded-xl border p-1.5 shadow-xl"
                            onPointerDown={(event) => event.stopPropagation()}
                            onMouseDown={(event) => event.stopPropagation()}
                            onTouchStart={(event) => event.stopPropagation()}
                        >
                            <button
                                type="button"
                                onClick={() => handleToggleFolderPin(folder)}
                                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-neutral-300 hover:bg-neutral-900"
                            >
                                <Pin size={14} className={folder.isPinned ? "fill-amber-300 text-amber-300" : ""} />
                                {folder.isPinned ? "Unpin" : "Pin"}
                            </button>
                            <button
                                type="button"
                                onClick={() => openCreateFolder(folder.id)}
                                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-neutral-300 hover:bg-neutral-900"
                            >
                                <FolderPlus size={14} />
                                Sub folder
                            </button>
                            <button
                                type="button"
                                onClick={() => openEditFolder(folder)}
                                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-neutral-300 hover:bg-neutral-900"
                            >
                                <FolderPen size={14} />
                                Edit
                            </button>
                            <button
                                type="button"
                                onClick={() => handleDeleteFolder(folder)}
                                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-rose-300 hover:bg-rose-950/30"
                            >
                                <Trash2 size={14} />
                                Recycle Bin
                            </button>
                        </div>
                    )}
                </div>
                {expanded && children.map((child) => renderFolder(child, depth + 1))}
            </div>
        );
    };

    return (
        <div className={`${isDesktopVaultLayout ? "bs-vault-desktop-notes" : "bs-mobile-notes"} bs-vault-page hidden-scrollbar flex h-screen flex-col overflow-hidden bg-[var(--bg-primary)] text-[var(--text-primary)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}>
            <header className="bs-vault-header bs-vault-mobile-shell sticky top-0 z-30 border-b border-neutral-800 bg-black/95 px-4 pb-3 pt-4 backdrop-blur">
                <div className="mx-auto max-w-7xl">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <h1 className="bs-message-page-title bs-vault-mobile-title truncate">
                                    Vault
                                    <span className="bs-vault-title-dot" aria-hidden="true" />
                                </h1>
                                <p className="text-xs font-medium text-neutral-400">Your private space</p>
                            </div>
                            <div className="flex shrink-0 items-center gap-2 md:hidden">
                                <button
                                    type="button"
                                    onClick={() => setMobileSearchOpen((open) => !open)}
                                    className={`bs-home-mobile-command bs-vault-mobile-command ${mobileSearchOpen || searchQuery ? "is-active" : ""}`}
                                    aria-label="Search notes"
                                >
                                    <Search size={18} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => openNewNote()}
                                    className="bs-home-mobile-command bs-vault-mobile-command border-violet-500/45 bg-violet-500/20 text-violet-200"
                                    aria-label="Create note"
                                >
                                    <Plus size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="hidden flex-col gap-3 md:flex md:flex-row md:items-center">
                            <div className="bs-vault-desktop-search-shell relative min-w-0 sm:w-80">
                                <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-cyan-300/80" />
                                <input
                                    type="search"
                                    value={searchQuery}
                                    onChange={(event) => setSearchQuery(event.target.value)}
                                    placeholder={vaultSearchPlaceholder}
                                    className="bs-vault-desktop-search-input h-10 w-full rounded-2xl border border-neutral-800 bg-neutral-950 py-2 pl-10 pr-9 text-sm font-semibold text-white outline-none transition placeholder:text-neutral-600 focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/10"
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        onClick={() => setSearchQuery("")}
                                        className="absolute right-2.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-md text-neutral-500 transition hover:text-white"
                                        aria-label="Clear Vault search"
                                        title="Clear"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => openNewNote()}
                                className="bs-primary-action bs-vault-new-note inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm"
                            >
                                <Plus size={17} />
                                New Note
                            </button>
                        </div>
                    </div>
                    {mobileSearchOpen && (
                        <div
                            className="bs-vault-mobile-search-sheet bs-vault-search-shell mt-3 md:hidden"
                            onPointerDownCapture={(event) => event.stopPropagation()}
                            onMouseDownCapture={(event) => event.stopPropagation()}
                            onTouchStartCapture={(event) => event.stopPropagation()}
                            onPointerDown={(event) => event.stopPropagation()}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className="flex items-center gap-2">
                                <Search size={17} className="shrink-0 text-cyan-200" />
                                <input
                                    ref={mobileSearchInputRef}
                                    type="search"
                                    value={searchQuery}
                                    onPointerDownCapture={(event) => event.stopPropagation()}
                                    onMouseDownCapture={(event) => event.stopPropagation()}
                                    onTouchStartCapture={(event) => event.stopPropagation()}
                                    onChange={(event) => setSearchQuery(event.target.value)}
                                    placeholder={vaultSearchPlaceholder}
                                    className="bs-mobile-search-input bs-vault-search-input min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none"
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        onClick={() => setSearchQuery("")}
                                        className="grid h-8 w-8 place-items-center rounded-md border border-neutral-800 bg-black/45 text-neutral-400"
                                        aria-label="Clear search"
                                    >
                                        <X size={15} />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </header>

            <div className="flex min-h-0 flex-1 overflow-hidden">
                <main className="mx-auto flex w-full max-w-7xl flex-1 overflow-hidden">
                    <aside className="bs-vault-sidebar hidden-scrollbar hidden w-72 shrink-0 overflow-y-auto border-r border-neutral-800 p-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden xl:block">
                        <div className="mb-7 space-y-1">
                            <SidebarButton
                                active={activeFilter === "all"}
                                icon={NotebookTabs}
                                label="All Notes"
                                count={activeFilter === "all" ? totalNotes : undefined}
                                iconClassName="text-sky-300"
                                onClick={() => setActiveFilter("all")}
                            />
                            <SidebarButton
                                active={activeFilter === "favourites"}
                                icon={Pin}
                                label="Pinned"
                                count={favouritesCount}
                                iconClassName="text-amber-300"
                                activeClassName="text-white"
                                onClick={() => setActiveFilter("favourites")}
                            />
                            <SidebarButton
                                active={activeFilter === "shared"}
                                icon={UsersRound}
                                label="Shared"
                                count={sharedTotalCount}
                                iconClassName="text-violet-300"
                                activeClassName="text-white"
                                onClick={() => setActiveFilter("shared")}
                            />
                            <div className="my-2 h-px bg-neutral-900" />
                            <SidebarButton
                                active={activeFilter === "recycle-bin"}
                                icon={Trash2}
                                label="Recycle Bin"
                                iconClassName="text-rose-300"
                                activeClassName="text-white"
                                onClick={() => setActiveFilter("recycle-bin")}
                            />
                        </div>

                        <div>
                            <div className="mb-3 flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={() => setFoldersCollapsed((prev) => !prev)}
                                    className="flex items-center gap-2 text-sm font-semibold text-neutral-400 transition-colors hover:text-neutral-200"
                                >
                                    {foldersCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                                    Folders
                                </button>
                                <button
                                    type="button"
                                    onClick={() => openCreateFolder(null)}
                                    className="rounded-md p-1.5 text-neutral-500 transition-colors hover:bg-neutral-900 hover:text-cyan-300"
                                    title="Create folder"
                                >
                                    <FolderPlus size={16} />
                                </button>
                            </div>

                            {!foldersCollapsed && (
                            <div className="space-y-1">
                                {(foldersByParent.get("root") || []).map((folder) => renderFolder(folder))}

                                {folders.length === 0 && (
                                    <button
                                        type="button"
                                        onClick={() => openCreateFolder(null)}
                                        className="w-full rounded-lg border border-dashed border-neutral-800 px-3 py-4 text-sm text-neutral-500 transition-colors hover:border-cyan-900 hover:text-cyan-300"
                                    >
                                        Create your first folder
                                    </button>
                                )}
                            </div>
                            )}
                        </div>
                    </aside>

                    <section className="bs-vault-main hidden-scrollbar min-w-0 flex-1 overflow-y-auto p-4 pb-24 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:p-6">
                        {selectedFolder && (
                            <div className="mb-3 flex items-center justify-between gap-3 border-b border-neutral-900 pb-3">
                                <div className="flex min-w-0 items-center gap-2.5">
                                    <button
                                        type="button"
                                        onClick={() => setActiveFilter(selectedFolder.parent || "all")}
                                        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-neutral-500 transition-colors hover:bg-neutral-900 hover:text-cyan-100"
                                        title="Back"
                                        aria-label="Back to parent folder"
                                    >
                                        <ArrowLeft size={15} />
                                    </button>
                                    <span
                                        className="h-3 w-3 shrink-0 rounded-sm"
                                        style={{ backgroundColor: selectedFolder.color || "#22d3ee" }}
                                    />
                                    {selectedFolder.isPinned && <Pin size={13} className="shrink-0 fill-amber-300 text-amber-300" />}
                                    <div className="min-w-0">
                                        <span className="block truncate text-base font-semibold text-white">{selectedFolder.name}</span>
                                        {folderPath.length > 1 && (
                                            <span className="block truncate text-[11px] font-semibold text-neutral-600">
                                                {folderPath.slice(0, -1).map((folder) => folder.name).join(" / ")}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex shrink-0 items-center gap-1">
                                    {childFoldersInView.length === 0 && (
                                        <button
                                            type="button"
                                            onClick={() => openCreateFolder(selectedFolder.id)}
                                            className="grid h-8 w-8 place-items-center rounded-lg text-neutral-500 transition-colors hover:bg-cyan-950/20 hover:text-cyan-100"
                                            title="Create folder inside"
                                            aria-label={`Create folder inside ${selectedFolder.name}`}
                                        >
                                            <FolderPlus size={15} />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => openEditFolder(selectedFolder)}
                                        className="grid h-8 w-8 place-items-center rounded-lg text-neutral-500 transition-colors hover:bg-neutral-900 hover:text-cyan-100"
                                        title="Edit folder"
                                        aria-label="Edit folder"
                                    >
                                        <FolderPen size={15} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleToggleFolderPin(selectedFolder)}
                                        className="grid h-8 w-8 place-items-center rounded-lg text-neutral-500 transition-colors hover:bg-neutral-900 hover:text-amber-200"
                                        title={selectedFolder.isPinned ? "Unpin folder" : "Pin folder"}
                                        aria-label={selectedFolder.isPinned ? "Unpin folder" : "Pin folder"}
                                    >
                                        <Pin size={15} className={selectedFolder.isPinned ? "fill-amber-300 text-amber-300" : ""} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleDeleteFolder(selectedFolder)}
                                        className="grid h-8 w-8 place-items-center rounded-lg text-neutral-500 transition-colors hover:bg-rose-950/20 hover:text-rose-200"
                                        title="Move to Recycle Bin"
                                        aria-label="Move folder to Recycle Bin"
                                    >
                                        <Trash2 size={15} />
                                    </button>
                                </div>
                            </div>
                        )}

                        {false && selectedItemCount > 0 && !isRecycleBin && (
                            <div className="pointer-events-none fixed inset-x-3 bottom-24 z-40 md:bottom-6">
                                <div
                                    className="pointer-events-auto mx-auto flex w-fit max-w-[calc(100vw-1.5rem)] items-center gap-1.5 overflow-x-auto rounded-2xl border bg-neutral-950/94 px-2 py-2 shadow-[0_0_44px_color-mix(in_srgb,var(--selection-accent)_22%,transparent)] backdrop-blur [border-color:color-mix(in_srgb,var(--selection-accent)_38%,#1f2937)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                                    style={selectionAccentStyle}
                                >
                                    <div className="hidden">
                                        <p className="text-sm font-black text-white">{selectedItemCount} selected</p>
                                        <p className="whitespace-nowrap text-[11px] font-semibold text-neutral-500">
                                            {selectedNotes.length} note{selectedNotes.length === 1 ? "" : "s"} · {selectedFolders.length} folder{selectedFolders.length === 1 ? "" : "s"}
                                        </p>
                                    </div>
                                    {isRecycleBin ? (
                                        <>
                                            <button type="button" onClick={handleBulkRestoreFromTrash} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-emerald-950/15 text-emerald-100 transition hover:bg-emerald-950/35" title="Restore selected" aria-label="Restore selected">
                                                <RotateCcw size={15} />
                                            </button>
                                            <button type="button" onClick={handleBulkDeleteForever} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-rose-950/15 text-rose-100 transition hover:bg-rose-950/35" title="Delete selected forever" aria-label="Delete selected forever">
                                                <Trash2 size={15} />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                    <button type="button" onClick={() => setBulkActionSheet("move")} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-[var(--selection-accent)] transition hover:bg-white/5" title="Move selected" aria-label="Move selected">
                                        <FolderIcon size={15} />
                                    </button>
                                    <button type="button" onClick={handleBulkCopy} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-neutral-200 transition hover:bg-neutral-900" title="Duplicate selected" aria-label="Duplicate selected">
                                        <Copy size={15} />
                                    </button>
                                    <button type="button" onClick={handleBulkPin} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-amber-100 transition hover:bg-amber-950/20" title="Pin selected" aria-label="Pin selected">
                                        <Pin size={15} />
                                    </button>
                                    <button type="button" onClick={() => setBulkActionSheet("tag")} disabled={selectedNotes.length === 0} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-neutral-200 transition hover:bg-neutral-900 disabled:cursor-not-allowed disabled:opacity-35" title={selectedNotes.length === 0 ? "Select notes to tag" : "Tag selected notes"} aria-label="Tag selected notes">
                                        <Tags size={15} />
                                    </button>
                                    <button type="button" onClick={() => setBulkActionSheet("share")} disabled={selectedNotes.length === 0} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-violet-100 transition hover:bg-violet-950/20 disabled:cursor-not-allowed disabled:opacity-35" title={selectedNotes.length === 0 ? "Select notes to share" : "Share selected notes"} aria-label="Share selected notes">
                                        <Share2 size={15} />
                                    </button>
                                    <button type="button" onClick={handleBulkTrash} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-rose-100 transition hover:bg-rose-950/25" title="Move selected to Recycle Bin" aria-label="Move selected to Recycle Bin">
                                        <Trash2 size={15} />
                                    </button>
                                        </>
                                    )}
                                    <button type="button" onClick={clearSelection} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-neutral-500 transition hover:bg-neutral-900 hover:text-white" title="Clear selection" aria-label="Clear selection">
                                        <X size={16} />
                                    </button>
                                </div>
                            </div>
                        )}

                        {selectedItemCount > 0 && !isRecycleBin && (
                            <VaultBulkSelectionRail
                                accentStyle={selectionAccentStyle}
                                selectedNotesCount={selectedNotes.length}
                                canEditSingle={selectedItemCount === 1}
                                onEdit={handleEditSelectedItem}
                                onMove={() => setBulkActionSheet("move")}
                                onCopy={handleBulkCopy}
                                onPin={handleBulkPin}
                                onTag={() => setBulkActionSheet("tag")}
                                onShare={() => setBulkActionSheet("share")}
                                onRecycle={handleBulkTrash}
                                onClear={clearSelection}
                            />
                        )}

                        {!inFolderView && (
                        <div className="mb-4 min-w-0 md:hidden">
                            <div className="bs-vault-mobile-tabs hidden-scrollbar flex min-w-0 max-w-full gap-1 overflow-x-auto py-1 pr-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                {[
                                    { id: "all", label: "Notes", icon: NotebookTabs, iconClassName: "text-cyan-300" },
                                    { id: "favourites", label: "Pins", icon: Pin, iconClassName: "text-amber-300" },
                                    { id: "shared", label: "People", icon: UsersRound, iconClassName: "text-violet-300" },
                                    { id: "recycle-bin", label: "Trash", icon: Trash2, iconClassName: "text-rose-300" },
                                ].map(({ id, label, icon: Icon, iconClassName }) => (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => setActiveFilter(id)}
                                        className={`bs-vault-mobile-tab flex min-h-11 w-[3.55rem] shrink-0 flex-col items-center justify-center gap-0.5 rounded-xl px-1 text-[10px] font-semibold transition ${
                                            activeFilter === id
                                                ? "text-cyan-100"
                                                : "text-neutral-400 hover:text-neutral-100"
                                        }`}
                                        aria-pressed={activeFilter === id}
                                        aria-label={label}
                                    >
                                        <Icon size={16} className={`${iconClassName} bs-vault-filter-icon ${activeFilter === id ? "is-active" : ""}`} />
                                        <span className="bs-vault-mobile-tab-label">{label}</span>
                                    </button>
                                ))}
                                {CONTENT_VIEW_OPTIONS.map(({ id, label, icon: Icon, iconClassName }) => (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => setContentView(id)}
                                        className={`bs-vault-mobile-view-filter inline-flex min-h-11 w-[3.8rem] shrink-0 flex-col items-center justify-center gap-0.5 rounded-xl px-1 text-[10px] font-semibold transition ${
                                            contentView === id
                                                ? "text-cyan-100"
                                                : "text-neutral-400 hover:text-neutral-100"
                                        }`}
                                        aria-pressed={contentView === id}
                                        aria-label={`Show ${label.toLowerCase()}`}
                                    >
                                        <Icon size={16} className={`${iconClassName} bs-vault-filter-icon ${contentView === id ? "is-active" : ""}`} />
                                        <span className="bs-vault-mobile-tab-label">{label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        )}

                        {!inFolderView && (
                            <div className="mb-4 hidden items-center justify-between gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.018] px-3 py-2 md:flex">
                                <span className="text-[11px] font-black uppercase tracking-[0.18em] text-neutral-600">View</span>
                                <div className="bs-vault-desktop-content-filters flex items-center gap-1">
                                    {CONTENT_VIEW_OPTIONS.map(({ id, label, icon: Icon, iconClassName }) => (
                                        <button
                                            key={id}
                                            type="button"
                                            onClick={() => setContentView(id)}
                                            className={`inline-flex h-9 items-center gap-2 rounded-xl px-3 text-xs font-black transition ${
                                                contentView === id
                                                    ? "bg-cyan-400/10 text-cyan-100 shadow-[0_0_22px_rgba(34,211,238,0.10)]"
                                                    : "text-neutral-400 hover:bg-white/[0.045] hover:text-neutral-100"
                                            }`}
                                            aria-pressed={contentView === id}
                                            title={`Show ${label.toLowerCase()}`}
                                        >
                                            <Icon size={15} className={`${iconClassName} ${contentView === id ? "drop-shadow-[0_0_8px_rgba(34,211,238,0.28)]" : ""}`} />
                                            <span>{label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {showMobileVaultSummary && (
                            <div className="mb-6 space-y-5 md:hidden">
                                {showFolderContent && (
                                    <section className="bs-vault-mobile-section">
                                        <div className="mb-2.5 flex items-center justify-between gap-3">
                                            <span className="inline-flex items-center gap-2 text-sm font-black text-neutral-200">
                                                <FolderIcon size={14} className="text-neutral-400" />
                                                Folders
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => openCreateFolder(null)}
                                                className="bs-vault-mobile-plus grid h-7 w-7 place-items-center rounded-lg border border-neutral-800 bg-black/35 text-violet-200"
                                                title="Create folder"
                                                aria-label="Create folder"
                                            >
                                                <Plus size={14} />
                                            </button>
                                        </div>
                                        <div className="space-y-2">
                                            {mobileFolderPreview.map((folder) => {
                                                const folderSelected = selectedFolderIds.has(folder.id);
                                                return (
                                                <div key={folder.id} className="relative">
                                                    <button
                                                        type="button"
                                                        onPointerDown={() => beginMobileSelectionPress(() => toggleFolderSelection(folder))}
                                                        onPointerUp={clearMobileSelectionPress}
                                                        onPointerLeave={clearMobileSelectionPress}
                                                        onPointerCancel={clearMobileSelectionPress}
                                                        onContextMenu={(event) => {
                                                            event.preventDefault();
                                                            if (mobileSelectionPressTriggeredRef.current) return;
                                                            mobileSelectionPressTriggeredRef.current = true;
                                                            toggleFolderSelection(folder);
                                                        }}
                                                        onClick={(event) => {
                                                            if (consumeMobileSelectionPress(event)) return;
                                                            if (selectionMode || folderSelected) {
                                                                event.preventDefault();
                                                                toggleFolderSelection(folder);
                                                                return;
                                                            }
                                                            setActiveFilter(folder.id);
                                                        }}
                                                        className={`bs-vault-mobile-row flex w-full items-center gap-3 rounded-xl border bg-[linear-gradient(145deg,rgba(14,14,28,0.94),rgba(5,7,14,0.96))] px-3 py-2.5 text-left ${
                                                            folderSelected ? "border-cyan-300/70 shadow-[0_0_22px_rgba(34,211,238,0.16)]" : "border-violet-500/20"
                                                        }`}
                                                        aria-pressed={folderSelected}
                                                    >
                                                        <span className="bs-vault-mobile-row-icon grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-neutral-800 bg-neutral-900/80">
                                                            <FolderIcon size={16} style={{ color: folder.color || "#22d3ee" }} />
                                                        </span>
                                                        <span className="min-w-0 flex-1">
                                                            <span className="block truncate text-sm font-semibold text-white">{folder.name}</span>
                                                            <span className="block text-xs text-neutral-400">{folder.noteCount} note{folder.noteCount === 1 ? "" : "s"}</span>
                                                        </span>
                                                        {folderSelected && <Check size={16} className="shrink-0 text-cyan-200" />}
                                                    </button>
                                                </div>
                                            )})}
                                        </div>
                                    </section>
                                )}

                                {showNoteContent && (
                                    <section className="bs-vault-mobile-section">
                                        <div className="mb-2.5 flex items-center justify-between gap-3">
                                            <span className="inline-flex items-center gap-2 text-sm font-black text-neutral-200">
                                                <ScrollText size={14} className="text-neutral-400" />
                                                Notes
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => openNewNote()}
                                                className="bs-vault-mobile-plus grid h-7 w-7 place-items-center rounded-lg border border-neutral-800 bg-black/35 text-violet-200"
                                                title="Create note"
                                                aria-label="Create note"
                                            >
                                                <Plus size={14} />
                                            </button>
                                        </div>

                                        <div className="space-y-2">
                                            {mobileNotesPreview.map((note) => {
                                                const noteSelected = selectedNoteIds.has(note.id);
                                                const noteSelectable = note.isOwner !== false;
                                                return (
                                                <div key={note.id} className="relative">
                                                    <button
                                                        type="button"
                                                        onPointerDown={() => {
                                                            if (noteSelectable) beginMobileSelectionPress(() => toggleNoteSelection(note));
                                                        }}
                                                        onPointerUp={clearMobileSelectionPress}
                                                        onPointerLeave={clearMobileSelectionPress}
                                                        onPointerCancel={clearMobileSelectionPress}
                                                        onContextMenu={(event) => {
                                                            if (!noteSelectable) return;
                                                            event.preventDefault();
                                                            if (mobileSelectionPressTriggeredRef.current) return;
                                                            mobileSelectionPressTriggeredRef.current = true;
                                                            toggleNoteSelection(note);
                                                        }}
                                                        onClick={(event) => {
                                                            if (consumeMobileSelectionPress(event)) return;
                                                            if (selectionMode || noteSelected) {
                                                                event.preventDefault();
                                                                if (noteSelectable) toggleNoteSelection(note);
                                                                return;
                                                            }
                                                            openNote(note);
                                                        }}
                                                        className={`bs-vault-mobile-row flex w-full items-center gap-3 rounded-xl border bg-[linear-gradient(145deg,rgba(14,14,28,0.94),rgba(5,7,14,0.96))] px-3 py-2.5 text-left ${
                                                            noteSelected ? "border-cyan-300/70 shadow-[0_0_22px_rgba(34,211,238,0.16)]" : "border-violet-500/20"
                                                        }`}
                                                        aria-pressed={noteSelected}
                                                    >
                                                        <span
                                                            className="bs-vault-mobile-row-icon grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-neutral-800 bg-neutral-900/80"
                                                            style={{ boxShadow: `0 0 14px ${(note.color || "#a855f7")}22` }}
                                                        >
                                                            <ScrollText size={15} style={{ color: note.color || "#a855f7" }} />
                                                        </span>
                                                        <span className="min-w-0 flex-1">
                                                            <span className="block truncate text-sm font-semibold text-white">{note.title}</span>
                                                            <span className="block text-xs text-neutral-400">{timeAgo(note.updatedAt)}</span>
                                                        </span>
                                                        {noteSelected && <Check size={16} className="shrink-0 text-cyan-200" />}
                                                    </button>
                                                </div>
                                            )})}
                                        </div>

                                        {!mobileShowAllNotes && visibleNotes.length > 3 && (
                                            <button
                                                type="button"
                                                onClick={() => setMobileShowAllNotes(true)}
                                                className="bs-vault-mobile-view-all mt-2 flex w-full items-center justify-between rounded-xl border border-neutral-800 bg-black/35 px-3 py-2.5 text-sm font-medium text-neutral-300"
                                            >
                                                <span>View all notes</span>
                                                <ChevronRight size={16} className="text-neutral-500" />
                                            </button>
                                        )}
                                    </section>
                                )}

                                {activeFilter === "all" && (
                                    <section className="bs-vault-safe-card overflow-hidden rounded-2xl border border-violet-500/20 bg-[radial-gradient(circle_at_50%_0%,rgba(139,92,246,0.26),transparent_58%),linear-gradient(145deg,rgba(10,10,24,0.95),rgba(5,7,14,0.97))] p-5 text-center">
                                        <div className="bs-vault-safe-icon mx-auto mb-3 grid h-14 w-14 place-items-center rounded-2xl border border-violet-400/35 bg-violet-500/12 text-violet-200 shadow-[0_0_24px_rgba(168,85,247,0.24)]">
                                            <VaultGlowBookIcon />
                                        </div>
                                        <h3 className="text-xl font-black text-white">Your ideas are safe here</h3>
                                        <p className="mx-auto mt-2 max-w-xs text-sm leading-6 text-neutral-300">
                                            Capture thoughts, save links, and organize your knowledge.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => openNewNote()}
                                            className="bs-vault-safe-action mt-4 inline-flex items-center gap-1.5 rounded-lg border border-violet-400/20 bg-violet-500/10 px-3 py-1.5 text-xs font-semibold text-violet-100"
                                        >
                                            <Plus size={13} />
                                            New note
                                        </button>
                                    </section>
                                )}
                            </div>
                        )}

                        {showFolderContent && foldersInView.length > 0 && !showMobileVaultSummary && (
                            <div className="mb-5">
                                <div className="mb-3 flex items-center justify-between gap-3">
                                    <span className="inline-flex items-center gap-2">
                                        <FolderIcon size={14} className="text-neutral-500" />
                                        <span className="text-sm font-black text-neutral-300">Folders</span>
                                        {foldersInView.length > FOLDER_BATCH_SIZE && (
                                            <span className="rounded-md border border-neutral-900 bg-black/35 px-2 py-0.5 text-[10px] font-bold text-neutral-500">
                                                {visibleFoldersInView.length}/{foldersInView.length}
                                            </span>
                                        )}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => openCreateFolder(isFolderFilter(activeFilter) ? activeFilter : null)}
                                        className="grid h-8 w-8 place-items-center rounded-lg border border-neutral-800 bg-black/35 text-cyan-200 transition-colors hover:border-cyan-800 hover:bg-cyan-950/20"
                                        title="Create folder"
                                        aria-label="Create folder"
                                    >
                                        <Plus size={15} />
                                    </button>
                                </div>
                                <div
                                    className="grid grid-cols-6 gap-x-2 gap-y-1 sm:grid-cols-8 md:grid-cols-11 2xl:grid-cols-12"
                                    onDragOver={(event) => {
                                        if (!draggedFolderId || (activeFilter !== "all" && !isFolderFilter(activeFilter))) return;
                                        event.preventDefault();
                                    }}
                                    onDrop={(event) => {
                                        if (!draggedFolderId || (activeFilter !== "all" && !isFolderFilter(activeFilter))) return;
                                        event.preventDefault();
                                        handleDropFolderIntoFolder(isFolderFilter(activeFilter) ? activeFilter : null);
                                    }}
                                >
                                    {visibleFoldersInView.map((folder) => (
                                        <FolderTile
                                            key={folder.id}
                                            folder={folder}
                                            selected={selectedFolderIds.has(folder.id)}
                                            selectionMode={selectionMode}
                                            onOpen={() => setActiveFilter(folder.id)}
                                            onSelect={toggleFolderSelection}
                                            onDropNote={() => handleDropNoteIntoFolder(folder.id)}
                                            onDragStartFolder={(dragged) => {
                                                setDraggedFolderId(dragged.id);
                                                setDraggedNoteId(null);
                                            }}
                                            onDragEndFolder={() => setDraggedFolderId(null)}
                                            onDropFolder={() => handleDropFolderIntoFolder(folder.id)}
                                            onEdit={openEditFolder}
                                            onTogglePin={handleToggleFolderPin}
                                            onTrash={handleDeleteFolder}
                                        />
                                    ))}
                                </div>
                                {foldersInView.length > FOLDER_BATCH_SIZE && (
                                    <div className="mt-3 flex items-center justify-center gap-2">
                                        {hiddenFolderCount > 0 ? (
                                            <button
                                                type="button"
                                                onClick={() => setFolderVisibleCount((count) => Math.min(foldersInView.length, count + FOLDER_BATCH_SIZE))}
                                                className="inline-flex items-center gap-2 rounded-lg border border-neutral-800 bg-black/45 px-3 py-2 text-xs font-bold text-neutral-300 transition-colors hover:border-cyan-800/70 hover:text-cyan-100"
                                            >
                                                <ChevronDown size={13} />
                                                Show more
                                                <span className="text-neutral-500">{hiddenFolderCount} left</span>
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => setFolderVisibleCount(FOLDER_BATCH_SIZE)}
                                                className="inline-flex items-center gap-2 rounded-lg border border-neutral-900 bg-black/30 px-3 py-2 text-xs font-bold text-neutral-500 transition-colors hover:border-neutral-700 hover:text-neutral-200"
                                            >
                                                Collapse
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {showFolderContent && isRecycleBin && deletedFolders.length > 0 && (
                            <div className="mb-7">
                                <div className="mb-3 flex items-center justify-between gap-3">
                                    <span className="inline-flex items-center gap-2">
                                        <FolderIcon size={14} className="text-neutral-500" />
                                        <span className="text-sm font-black text-neutral-300">Folders</span>
                                    </span>
                                    {recycleHeaderActions}
                                </div>
                                <div className="grid grid-cols-6 gap-x-2 gap-y-1 sm:grid-cols-8 md:grid-cols-11 2xl:grid-cols-12">
                                    {visibleDeletedFolders.map((folder) => {
                                        const folderSelected = selectedFolderIds.has(folder.id);
                                        return (
                                        <button
                                            type="button"
                                            key={folder.id}
                                            onClick={() => toggleFolderSelection(folder)}
                                            onKeyDown={(event) => {
                                                if (event.key !== "Enter" && event.key !== " ") return;
                                                event.preventDefault();
                                                toggleFolderSelection(folder);
                                            }}
                                            aria-pressed={folderSelected}
                                            title={folder.name}
                                            className={`bs-vault-folder-tile bs-vault-tile-bare group relative flex min-w-0 flex-col items-center gap-1 rounded-2xl border border-transparent bg-transparent px-0 py-1 text-center transition-colors hover:border-transparent hover:bg-transparent ${
                                                folderSelected
                                                    ? "ring-2 ring-cyan-300/65 shadow-[0_0_24px_rgba(34,211,238,0.18)]"
                                                    : ""
                                            }`}
                                        >
                                            {folderSelected && <span className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-cyan-300 shadow-[0_0_14px_rgba(34,211,238,0.8)]" aria-hidden="true" />}
                                            <span
                                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-neutral-800 bg-neutral-900/80 shadow-[0_0_12px_rgba(8,145,178,0.05)] transition-transform group-hover:-translate-y-0.5"
                                                style={{ boxShadow: `0 0 11px ${folder.color || "#22d3ee"}22` }}
                                            >
                                                <FolderIcon size={18} style={{ color: folder.color }} />
                                            </span>
                                            <span title={folder.name} className="block w-16 max-w-full truncate text-[10px] font-medium text-white sm:text-[11px]">{folder.name}</span>
                                        </button>
                                        );
                                    })}
                                </div>
                                {deletedFolders.length > FOLDER_BATCH_SIZE && (
                                    <div className="mt-3 flex items-center justify-center gap-2">
                                        {hiddenDeletedFolderCount > 0 ? (
                                            <button
                                                type="button"
                                                onClick={() => setDeletedFolderVisibleCount((count) => Math.min(deletedFolders.length, count + FOLDER_BATCH_SIZE))}
                                                className="inline-flex items-center gap-2 rounded-lg border border-neutral-800 bg-black/45 px-3 py-2 text-xs font-bold text-neutral-300 transition-colors hover:border-rose-800/70 hover:text-rose-100"
                                            >
                                                <ChevronDown size={13} />
                                                Show more
                                                <span className="text-neutral-500">{hiddenDeletedFolderCount} left</span>
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => setDeletedFolderVisibleCount(FOLDER_BATCH_SIZE)}
                                                className="inline-flex items-center gap-2 rounded-lg border border-neutral-900 bg-black/30 px-3 py-2 text-xs font-bold text-neutral-500 transition-colors hover:border-neutral-700 hover:text-neutral-200"
                                            >
                                                Collapse
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {isRecycleBin && !loading && !notesTransitioning && recycleItemCount === 0 && (
                            <div className="bs-vault-empty-state bs-vault-empty-bin mt-8 overflow-hidden rounded-3xl px-6 py-14 text-center">
                                <span className="bs-vault-empty-bin-icon mx-auto mb-5 grid h-16 w-16 place-items-center rounded-3xl">
                                    <Trash2 size={28} />
                                </span>
                                <h3 className="text-xl font-black text-white">Recycle Bin is empty</h3>
                                <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-neutral-500">
                                    Deleted notes and folders will appear here before permanent removal.
                                </p>
                            </div>
                        )}

                        {showFolderContent && !showNoteContent && activeFilter !== "recycle-bin" && !loading && foldersInView.length === 0 && !showMobileVaultSummary && (
                            <div className="bs-vault-empty-state mt-8 rounded-lg border border-neutral-800 bg-neutral-950 px-6 py-14 text-center">
                                <FolderIcon size={46} className="mx-auto mb-4 text-neutral-700" />
                                <h3 className="text-lg font-semibold text-neutral-300">
                                    {searchQuery ? "No folders matched" : activeFilter === "recycle-bin" ? "No deleted folders" : "No folders here"}
                                </h3>
                                <p className="mx-auto mt-2 max-w-md text-sm text-neutral-500">
                                    {searchQuery ? "Try another folder name." : "Create a folder to organise this part of Vault."}
                                </p>
                                {!searchQuery && activeFilter !== "recycle-bin" && (
                                    <button
                                        type="button"
                                        onClick={() => openCreateFolder(isFolderFilter(activeFilter) ? activeFilter : null)}
                                        className="bs-primary-action mt-5 inline-flex items-center gap-2 px-4 py-2 text-sm"
                                    >
                                        <FolderPlus size={16} />
                                        New folder
                                    </button>
                                )}
                            </div>
                        )}

                        {showNoteContent && activeFilter !== "shared" && !(isRecycleBin && recycleItemCount === 0) && !showMobileVaultSummary && (
                            <div className="mb-3 flex items-center justify-between gap-3">
                                <span className="inline-flex items-center gap-2">
                                    <ScrollText size={14} className="text-neutral-500" />
                                    <span className="text-sm font-black text-neutral-300">Notes</span>
                                    {activeFilter !== "shared" && visibleNotes.length > NOTE_BATCH_SIZE && (
                                        <span className="rounded-md border border-neutral-900 bg-black/35 px-2 py-0.5 text-[10px] font-bold text-neutral-500">
                                            {visibleNotesForGrid.length}/{Math.max(totalNotes, visibleNotes.length)}
                                        </span>
                                    )}
                                </span>
                                {isRecycleBin && deletedFolders.length === 0 && recycleHeaderActions}
                                {!loading && activeFilter !== "recycle-bin" && (
                                    <button
                                        type="button"
                                        onClick={() => openNewNote()}
                                        className="grid h-8 w-8 place-items-center rounded-lg border border-neutral-800 bg-black/35 text-cyan-200 transition-colors hover:border-cyan-800 hover:bg-cyan-950/20"
                                        title="Create note"
                                        aria-label="Create note"
                                    >
                                        <Plus size={15} />
                                    </button>
                                )}
                            </div>
                        )}

                        {!showMobileVaultSummary && (showNoteContent ? (activeFilter === "shared" && !loading && !notesTransitioning ? (
                            <div className="space-y-7">
                                <section className="rounded-lg border border-neutral-800 bg-neutral-950/70 p-3 sm:p-4">
                                    <div className="mb-4 flex flex-wrap items-center gap-3 border-b border-neutral-800 pb-3">
                                        <span className="flex h-9 w-9 items-center justify-center rounded-md border border-emerald-900/60 bg-emerald-950/30 text-emerald-300">
                                            <Inbox size={17} />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <h3 className="text-sm font-bold text-white">Shared with me</h3>
                                            <p className="text-xs text-neutral-500">Notes other people have opened for you.</p>
                                        </div>
                                        <span className="rounded-md border border-neutral-800 bg-black px-2 py-1 text-xs font-semibold text-neutral-400">
                                            {sharedWithDisplayCount}
                                        </span>
                                    </div>
                                    {sharedWithMeNotes.length > 0 ? (
                                        <div className={noteGridClass}>
                                            {sharedWithMeNotes.map((note) => (
                                                <NoteCard
                                                    key={note.id}
                                                    note={note}
                                                    activeFilter={activeFilter}
                                                    viewMode={viewMode}
                                                    panelOpen={Boolean(selectedNote && !isDesktopVaultLayout)}
                                                    openSlot={openNoteSlots.get(note.id)}
                                                    selectable
                                                    selected={selectedNoteIds.has(note.id)}
                                                    selectionMode={selectionMode}
                                                    preferSelectOnClick={false}
                                                    onOpen={() => openNote(note)}
                                                    onSelect={toggleNoteSelection}
                                                    onDragStart={(dragged) => setDraggedNoteId(dragged.id)}
                                                    onMoveRequest={handleMoveNoteRequest}
                                                    onTogglePin={handleTogglePin}
                                                    onTrash={handleTrashNote}
                                                    onRestore={handleRestoreFromTrash}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="rounded-lg border border-dashed border-neutral-800 bg-black/30 px-4 py-8 text-center">
                                            <p className="text-sm font-semibold text-neutral-300">No shared notes yet</p>
                                            <p className="mt-1 text-sm text-neutral-500">Notes other people share with you will appear here.</p>
                                        </div>
                                    )}
                                </section>

                                <section className="rounded-lg border border-neutral-800 bg-neutral-950/70 p-3 sm:p-4">
                                    <div className="mb-4 flex flex-wrap items-center gap-3 border-b border-neutral-800 pb-3">
                                        <span className="flex h-9 w-9 items-center justify-center rounded-md border border-cyan-900/60 bg-cyan-950/30 text-cyan-300">
                                            <Send size={17} />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <h3 className="text-sm font-bold text-white">Shared by me</h3>
                                            <p className="text-xs text-neutral-500">Your notes that currently have other people on them.</p>
                                        </div>
                                        <span className="rounded-md border border-neutral-800 bg-black px-2 py-1 text-xs font-semibold text-neutral-400">
                                            {sharedByDisplayCount}
                                        </span>
                                    </div>
                                    {sharedByMeNotes.length > 0 ? (
                                        <div className={noteGridClass}>
                                            {sharedByMeNotes.map((note) => (
                                                <NoteCard
                                                    key={note.id}
                                                    note={note}
                                                    activeFilter={activeFilter}
                                                    viewMode={viewMode}
                                                    panelOpen={Boolean(selectedNote && !isDesktopVaultLayout)}
                                                    openSlot={openNoteSlots.get(note.id)}
                                                    selectable
                                                    selected={selectedNoteIds.has(note.id)}
                                                    selectionMode={selectionMode}
                                                    preferSelectOnClick={false}
                                                    onOpen={() => openNote(note)}
                                                    onSelect={toggleNoteSelection}
                                                    onDragStart={(dragged) => setDraggedNoteId(dragged.id)}
                                                    onMoveRequest={handleMoveNoteRequest}
                                                    onTogglePin={handleTogglePin}
                                                    onTrash={handleTrashNote}
                                                    onRestore={handleRestoreFromTrash}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="rounded-lg border border-dashed border-neutral-800 bg-black/30 px-4 py-8 text-center">
                                            <p className="text-sm font-semibold text-neutral-300">Nothing shared by you yet</p>
                                            <p className="mt-1 text-sm text-neutral-500">Create a note, open it, and give someone access.</p>
                                            <button
                                                type="button"
                                                onClick={() => openNewNote()}
                                                className="bs-primary-action mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm"
                                            >
                                                <Plus size={16} />
                                                Create note
                                            </button>
                                        </div>
                                    )}
                                </section>
                            </div>
                        ) : (
                            <div className={noteGridClass}>
                                {notesLoadingVisible
                                    ? Array.from({ length: 9 }).map((_, index) => <NoteSkeleton key={index} />)
                                    : visibleNotesForGrid.map((note) => (
                                        <NoteCard
                                            key={note.id}
                                            note={note}
                                            activeFilter={activeFilter}
                                            viewMode={viewMode}
                                            panelOpen={Boolean(selectedNote && !isDesktopVaultLayout)}
                                            openSlot={openNoteSlots.get(note.id)}
                                            selectable
                                            selected={selectedNoteIds.has(note.id)}
                                            selectionMode={selectionMode}
                                            preferSelectOnClick={false}
                                            onOpen={() => {
                                                openNote(note);
                                            }}
                                            onSelect={toggleNoteSelection}
                                            onDragStart={(dragged) => setDraggedNoteId(dragged.id)}
                                            onMoveRequest={handleMoveNoteRequest}
                                            onTogglePin={handleTogglePin}
                                            onTrash={handleTrashNote}
                                            onRestore={handleRestoreFromTrash}
                                        />
                                    ))}
                            </div>
                        )) : null)}

                        {showNoteContent && !loading && !notesTransitioning && visibleNotes.length === 0 && activeFilter !== "shared" && activeFilter !== "recycle-bin" && !showMobileVaultSummary && (
                            <div className="bs-vault-empty-state mt-8 rounded-lg border border-neutral-800 bg-neutral-950 px-6 py-14 text-center">
                                <NotebookTabs size={48} className="mx-auto mb-4 text-neutral-700" />
                                <h3 className="text-lg font-semibold text-neutral-300">
                                    {searchQuery ? "Nothing matched" : activeFilter === "recycle-bin" ? "Recycle Bin is empty" : "Vault is quiet"}
                                </h3>
                                <p className="mx-auto mt-2 max-w-md text-sm text-neutral-500">
                                    {searchQuery
                                        ? "Try a different word, tag, folder, or note type."
                                        : activeFilter === "recycle-bin"
                                            ? "Deleted notes will wait here before permanent removal."
                                            : "Capture a note, checklist, code snippet, or journal entry."}
                                </p>
                                {!searchQuery && activeFilter !== "recycle-bin" && activeFilter !== "shared" && (
                                    <button
                                        type="button"
                                        onClick={() => openNewNote()}
                                        className="bs-primary-action mt-5 inline-flex items-center gap-2 px-4 py-2 text-sm"
                                    >
                                        <Plus size={16} />
                                        Start writing
                                    </button>
                                )}
                            </div>
                        )}

                        {showNoteContent && activeFilter !== "shared" && !loading && !notesTransitioning && visibleNotes.length > 0 && !showMobileVaultSummary && (hiddenLoadedNoteCount > 0 || hasMore || visibleNotes.length > NOTE_BATCH_SIZE) && (
                            <div className="mt-4 flex items-center justify-center gap-2">
                                {hiddenLoadedNoteCount > 0 || hasMore ? (
                                    <button
                                        type="button"
                                        onClick={handleShowMoreNotes}
                                        disabled={loadingMore}
                                        className="inline-flex items-center gap-2 rounded-lg border border-neutral-800 bg-black/45 px-3 py-2 text-xs font-bold text-neutral-300 transition-colors hover:border-cyan-800/70 hover:text-cyan-100 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {loadingMore ? <Loader2 size={13} className="animate-spin" /> : <ChevronDown size={13} />}
                                        {loadingMore ? "Loading more..." : "Show more"}
                                        <span className="text-neutral-500">{remainingNoteCount} left</span>
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => setNoteVisibleCount(NOTE_BATCH_SIZE)}
                                        className="inline-flex items-center gap-2 rounded-lg border border-neutral-900 bg-black/30 px-3 py-2 text-xs font-bold text-neutral-500 transition-colors hover:border-neutral-700 hover:text-neutral-200"
                                    >
                                        Collapse
                                    </button>
                                )}
                            </div>
                        )}
                    </section>

                    {selectedNote && (
                        <aside
                            className="bs-vault-editor-dock hidden-scrollbar fixed inset-0 z-50 flex w-full overflow-hidden bg-black/70 backdrop-blur-sm [scrollbar-width:none] [&::-webkit-scrollbar]:hidden xl:static xl:z-auto xl:w-[480px] xl:shrink-0 xl:bg-transparent xl:backdrop-blur-none"
                        >
                            <div
                                className="bs-vault-editor-shell hidden-scrollbar ml-auto flex h-full w-full flex-col overflow-hidden border-l shadow-2xl [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                                style={{ "--note-accent": selectedNote.color || OPEN_COLORS[0] } as React.CSSProperties}
                            >
                                <div className="bs-vault-editor-topbar border-b border-neutral-900/80 p-3">
                                    <div className="mb-3 flex items-center justify-between gap-3">
                                        <h2 className="text-sm font-bold uppercase tracking-wide text-neutral-400">
                                            Active Note
                                        </h2>
                                        <button
                                            type="button"
                                            onClick={closeOpenNote}
                                            className="rounded-md px-2 py-1 text-xs font-semibold text-neutral-500 transition-colors hover:bg-neutral-900 hover:text-white"
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>

                                <div className="hidden-scrollbar min-h-0 flex-1 overflow-y-auto p-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                    <OpenNotePane
                                        key={selectedNote.id}
                                        note={selectedNote}
                                        slot={{
                                            index: 1,
                                            color: selectedNote.color || OPEN_COLORS[0],
                                            active: true,
                                        }}
                                        active
                                        folders={folders}
                                        onActivate={() => setSelectedNote(selectedNote)}
                                        onClose={closeOpenNote}
                                        onUpdated={(updated) => {
                                            updateOpenNote(updated);
                                            loadQuickCounts();
                                            loadFolders();
                                        }}
                                        onRemove={(noteId) => {
                                            removeNoteFromView(noteId);
                                            loadQuickCounts();
                                            loadFolders();
                                        }}
                                        onError={setError}
                                        onNotice={setNotice}
                                        onDirtyChange={setSelectedNoteDirty}
                                        onConfirmDiscard={(discard) => requestConfirm({
                                            title: "Discard edits?",
                                            message: "The current note edits have not been saved.",
                                            confirmLabel: "Discard edits",
                                            tone: "danger",
                                            onConfirm: discard,
                                        })}
                                    />
                                </div>
                            </div>
                        </aside>
                    )}
                </main>
            </div>

            {(error || notice) && (
                <div className="pointer-events-none fixed inset-x-3 top-4 z-[90] flex justify-center sm:top-6 sm:justify-end sm:pr-4">
                    {error ? (
                        <div className="pointer-events-auto max-w-sm rounded-2xl border border-rose-900/70 bg-black/90 px-4 py-3 text-sm font-semibold text-rose-100 shadow-[0_0_34px_rgba(244,63,94,0.22)] backdrop-blur">
                            {error}
                        </div>
                    ) : (
                        <div className="pointer-events-auto flex max-w-sm items-center gap-2 rounded-2xl border border-cyan-900/70 bg-black/90 px-4 py-3 text-sm font-semibold text-cyan-100 shadow-[0_0_34px_rgba(8,145,178,0.28)] backdrop-blur">
                            <Check size={16} className="shrink-0 text-cyan-300" />
                            <span>{notice}</span>
                        </div>
                    )}
                </div>
            )}

            <div className="sticky bottom-0 z-30 bg-black">
                <FooterNav />
            </div>

            {commandOpen && (
                <ModalShell onClose={() => setCommandOpen(false)} title="Vault command">
                    <div className="bs-vault-modal-header border-b border-neutral-800 p-5">
                        <h2 className="text-lg font-black text-white">Vault command</h2>
                        <p className="mt-1 text-sm text-neutral-500">Find notes, jump views, create fast, or open a tag.</p>
                    </div>
                    <div className="space-y-4 p-5">
                        <input
                            type="search"
                            value={commandQuery}
                            onChange={(event) => setCommandQuery(event.target.value)}
                            placeholder="Search note, tag, or action..."
                            className="bs-vault-title-input w-full rounded-xl border border-neutral-800 bg-neutral-950 px-4 py-3 text-sm font-semibold text-white outline-none"
                            autoFocus
                        />
                        <div className="grid gap-2 sm:grid-cols-2">
                            {[
                                ["All notes", "all"],
                                ["Pinned", "favourites"],
                                ["Shared", "shared"],
                                ["Recycle Bin", "recycle-bin"],
                            ].map(([label, filter]) => (
                                <button
                                    key={filter}
                                    type="button"
                                    onClick={() => {
                                        setActiveFilter(filter);
                                        setCommandOpen(false);
                                    }}
                                    className="rounded-lg border border-neutral-800 bg-black/35 px-3 py-2 text-left text-sm font-bold text-neutral-200 hover:border-cyan-800"
                                >
                                    {label}
                                </button>
                            ))}
                            <button
                                type="button"
                                onClick={() => {
                                    setCommandOpen(false);
                                    openNewNote();
                                }}
                                className="rounded-lg border border-cyan-900/60 bg-cyan-950/20 px-3 py-2 text-left text-sm font-bold text-cyan-100 hover:bg-cyan-950/35"
                            >
                                New note
                            </button>
                        </div>
                        {commandMatches.noteMatches.length > 0 && (
                            <div>
                                <p className="mb-2 text-xs font-black uppercase tracking-[0.14em] text-neutral-500">Notes</p>
                                <div className="space-y-2">
                                    {commandMatches.noteMatches.map((note) => (
                                        <button
                                            key={note.id}
                                            type="button"
                                            onClick={() => {
                                                openNote(note);
                                                setCommandOpen(false);
                                            }}
                                            className="flex w-full items-center justify-between gap-3 rounded-lg border border-neutral-800 bg-black/35 px-3 py-2 text-left hover:border-cyan-800"
                                        >
                                            <span className="min-w-0">
                                                <span className="block truncate text-sm font-bold text-white">{note.title}</span>
                                                <span className="text-xs text-neutral-500">{timeAgo(note.updatedAt)}</span>
                                            </span>
                                            <ChevronRight size={15} className="text-neutral-600" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {commandMatches.folderMatches.length > 0 && (
                            <div>
                                <p className="mb-2 text-xs font-black uppercase tracking-[0.14em] text-neutral-500">Folders</p>
                                <div className="grid gap-2 sm:grid-cols-2">
                                    {commandMatches.folderMatches.map((folder) => (
                                        <button
                                            key={folder.id}
                                            type="button"
                                            onClick={() => {
                                                setActiveFilter(folder.id);
                                                setCommandOpen(false);
                                            }}
                                            className="flex items-center gap-2 rounded-lg border border-neutral-800 bg-black/35 px-3 py-2 text-left hover:border-cyan-800"
                                        >
                                            <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: folder.color }} />
                                            <span className="min-w-0 flex-1 truncate text-sm font-bold text-white">{folder.name}</span>
                                            {folder.isPinned && <Pin size={12} className="fill-amber-300 text-amber-300" />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {commandMatches.tagMatches.length > 0 && (
                            <div>
                                <p className="mb-2 text-xs font-black uppercase tracking-[0.14em] text-neutral-500">Tags</p>
                                <div className="flex flex-wrap gap-2">
                                    {commandMatches.tagMatches.map(([tag, count]) => (
                                        <button
                                            key={tag}
                                            type="button"
                                            onClick={() => {
                                                setSearchQuery(tag);
                                                setActiveFilter("all");
                                                setCommandOpen(false);
                                            }}
                                            className="rounded-full border border-neutral-800 bg-black/45 px-3 py-2 text-xs font-bold text-neutral-300 hover:border-cyan-800 hover:text-cyan-100"
                                        >
                                            #{tag} <span className="text-neutral-600">{count}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </ModalShell>
            )}

            {moveNoteTarget && (
                <ModalShell onClose={() => setMoveNoteTarget(null)} title="Move note">
                    <div className="bs-vault-modal-header border-b border-neutral-800 p-5">
                        <h2 className="text-lg font-black text-white">Move note</h2>
                        <p className="mt-1 truncate text-sm text-neutral-500">{moveNoteTarget.title}</p>
                    </div>
                    <div className="space-y-2 p-5">
                        <button
                            type="button"
                            onClick={async () => {
                                await moveNoteToFolder(moveNoteTarget.id, null);
                                await loadFolders();
                                setMoveNoteTarget(null);
                                setNotice("Note removed from folder");
                            }}
                            className="flex w-full items-center justify-between rounded-lg border border-neutral-800 bg-black/35 px-3 py-3 text-left text-sm font-bold text-neutral-200 hover:border-cyan-800"
                        >
                            No folder
                            <FolderIcon size={15} className="text-neutral-400" />
                        </button>
                        {folders.map((folder) => (
                            <button
                                key={folder.id}
                                type="button"
                                onClick={async () => {
                                    await moveNoteToFolder(moveNoteTarget.id, folder.id);
                                    await loadFolders();
                                    setMoveNoteTarget(null);
                                    setNotice(`Moved to "${folder.name}"`);
                                }}
                                className="flex w-full items-center gap-3 rounded-lg border border-neutral-800 bg-black/35 px-3 py-3 text-left hover:border-cyan-800"
                            >
                                <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: folder.color }} />
                                <span className="min-w-0 flex-1 truncate text-sm font-bold text-white">{folder.name}</span>
                                {folder.isPinned && <Pin size={12} className="fill-amber-300 text-amber-300" />}
                            </button>
                        ))}
                    </div>
                </ModalShell>
            )}

            {showFolderModal && (
                <ModalShell onClose={requestCloseFolderModal} title={editingFolder ? "Edit folder" : "New folder"} dismissOnBackdrop={false}>
                    <div className="bs-vault-modal-header flex items-center justify-between border-b border-neutral-800 p-5">
                        <h2 className="flex items-center gap-2 text-lg font-bold text-white">
                            {editingFolder ? <FolderPen size={20} className="text-cyan-300" /> : <FolderPlus size={20} className="text-cyan-300" />}
                            {editingFolder ? "Edit Folder" : "New Folder"}
                        </h2>
                        <button
                            type="button"
                            onClick={requestCloseFolderModal}
                            className="rounded-md p-2 text-neutral-500 hover:bg-neutral-900 hover:text-white"
                        >
                            <X size={18} />
                        </button>
                    </div>
                    <div className="space-y-4 p-5">
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={folderName}
                                onChange={(event) => setFolderName(event.target.value)}
                                onKeyDown={(event) => event.key === "Enter" && handleSaveFolder()}
                                placeholder="Folder name..."
                                className="bs-vault-soft-input min-w-0 flex-1 rounded-lg border border-neutral-800 bg-neutral-950 px-4 py-3 text-white outline-none placeholder:text-neutral-600 focus:border-cyan-800"
                                autoFocus
                            />
                            <label
                                className="grid h-12 w-12 shrink-0 cursor-pointer place-items-center overflow-hidden rounded-lg border border-neutral-800 shadow-[0_0_22px_rgba(34,211,238,0.08)] transition hover:border-cyan-800"
                                style={{ backgroundColor: folderColor }}
                                title="Pick folder colour"
                            >
                                <span className="sr-only">Pick folder colour</span>
                                <input
                                    type="color"
                                    value={folderColor}
                                    onChange={(event) => setFolderColor(event.target.value)}
                                    className="h-16 w-16 cursor-pointer opacity-0"
                                    aria-label="Pick folder colour"
                                />
                            </label>
                        </div>

                        <label className="block">
                            <span className="mb-2 block text-sm text-neutral-500">Inside folder</span>
                            <select
                                value={folderParent || ""}
                                onChange={(event) => setFolderParent(event.target.value || null)}
                                className="bs-vault-soft-input w-full rounded-lg border border-neutral-800 bg-neutral-950 px-4 py-3 text-sm text-white outline-none focus:border-cyan-800"
                            >
                                <option value="">Top level</option>
                                {folders
                                    .filter((folder) => !blockedFolderParentIds.has(folder.id))
                                    .map((folder) => (
                                        <option key={folder.id} value={folder.id}>
                                            {folder.name}
                                        </option>
                                    ))}
                            </select>
                        </label>

                        <button
                            type="button"
                            onClick={handleSaveFolder}
                            disabled={!folderName.trim() || folderSaving}
                            className="bs-vault-modal-action inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50"
                            style={{ "--vault-action-color": folderColor } as React.CSSProperties}
                        >
                            {folderSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                            {editingFolder ? "Save Folder" : "Create Folder"}
                        </button>
                    </div>
                </ModalShell>
            )}

            {showNoteModal && (
                <ModalShell onClose={requestCloseNoteModal} title="New Vault Note" dismissOnBackdrop={false} wide>
                    <div className="bs-vault-modal-header flex items-center justify-between border-b border-neutral-800 p-5">
                        <h2 className="flex items-center gap-2 text-lg font-bold text-white">
                            <Plus size={20} className="text-cyan-300" />
                            New Vault Note
                        </h2>
                        <button
                            type="button"
                            onClick={requestCloseNoteModal}
                            className="rounded-md p-2 text-neutral-500 hover:bg-neutral-900 hover:text-white"
                        >
                            <X size={18} />
                        </button>
                    </div>
                    <div className="space-y-4 p-5">
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={newTitle}
                                onChange={(event) => setNewTitle(event.target.value)}
                                placeholder="Note title..."
                                 className="bs-vault-title-input bs-vault-single-surface-input min-w-0 flex-1 rounded-lg border px-4 py-3 text-base font-semibold text-white outline-none placeholder:text-neutral-600"
                                autoFocus
                            />
                            <label
                                className="grid h-12 w-12 shrink-0 cursor-pointer place-items-center overflow-hidden rounded-lg border border-neutral-800 shadow-[0_0_22px_rgba(34,211,238,0.08)] transition hover:border-cyan-800"
                                style={{ backgroundColor: newColor }}
                                title="Pick note colour"
                            >
                                <span className="sr-only">Pick note colour</span>
                                <input
                                    type="color"
                                    value={newColor}
                                    onChange={(event) => setNewColor(event.target.value)}
                                    className="h-16 w-16 cursor-pointer opacity-0"
                                    aria-label="Pick note colour"
                                />
                            </label>
                        </div>

                        <RichEditor
                            value={newContent}
                            onChange={setNewContent}
                            rows={8}
                            historyKey={`new-${showNoteModal}`}
                            placeholder="Write something worth keeping..."
                        />

                         <details className="bs-vault-details rounded-xl border">
                             <summary className="bs-vault-details-summary flex cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-bold text-neutral-200">
                                Details
                                <ChevronDown size={15} className="text-neutral-500" />
                            </summary>
                             <div className="bs-vault-details-body space-y-4 border-t p-4">
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <label className="block">
                                         <span className="mb-2 flex items-center gap-2 text-xs font-semibold text-neutral-500">
                                            <Tags size={13} />
                                            Tags
                                        </span>
                                        <input
                                            type="text"
                                            value={newTags}
                                            onChange={(event) => setNewTags(event.target.value)}
                                            placeholder="ideas, code, plans"
                                             className="bs-vault-soft-input bs-vault-single-surface-input w-full rounded-lg border px-3 py-2.5 text-sm text-white outline-none placeholder:text-neutral-600"
                                        />
                                    </label>
                                    <label className="block">
                                         <span className="mb-2 flex items-center gap-2 text-xs font-semibold text-neutral-500">
                                            <FolderIcon size={13} />
                                            Folder
                                        </span>
                                        <select
                                            value={newFolder || ""}
                                            onChange={(event) => setNewFolder(event.target.value || null)}
                                             className="bs-vault-soft-input bs-vault-single-surface-input w-full rounded-lg border px-3 py-2.5 text-sm text-white outline-none"
                                        >
                                            <option value="">No folder</option>
                                            {folders.map((folder) => (
                                                <option key={folder.id} value={folder.id}>
                                                    {folder.name}
                                                </option>
                                            ))}
                                        </select>
                                        {smartFolderSuggestions.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                {smartFolderSuggestions.map((folder) => (
                                                    <button
                                                        key={folder.id}
                                                        type="button"
                                                        onClick={() => setNewFolder(folder.id)}
                                                        className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[11px] font-bold transition-colors ${
                                                            newFolder === folder.id
                                                                ? "border-cyan-700 bg-cyan-950/40 text-cyan-100"
                                                                : "border-neutral-800 bg-black/35 text-neutral-400 hover:border-cyan-800 hover:text-cyan-100"
                                                        }`}
                                                    >
                                                        <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: folder.color }} />
                                                        {folder.name}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </label>
                                </div>
                            </div>
                        </details>

                        <button
                            type="button"
                            onClick={handleCreateNote}
                            disabled={!newTitle.trim() || noteSaving}
                            className="bs-vault-modal-action inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50"
                            style={{ "--vault-action-color": newColor } as React.CSSProperties}
                        >
                            {noteSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                            Save to Vault
                        </button>
                    </div>
                </ModalShell>
            )}

            <VaultBulkActionSheets
                sheet={bulkActionSheet}
                accent={selectionAccent}
                accentStyle={selectionAccentStyle}
                folders={folders}
                selectedItemCount={selectedItemCount}
                selectedNotesCount={selectedNotes.length}
                bulkFolder={bulkFolder}
                bulkTags={bulkTags}
                bulkShareUsername={bulkShareUsername}
                onClose={() => setBulkActionSheet(null)}
                onSetBulkFolder={setBulkFolder}
                onSetBulkTags={setBulkTags}
                onSetBulkShareUsername={setBulkShareUsername}
                onMove={handleBulkMove}
                onTag={handleBulkTag}
                onShare={handleBulkShare}
            />

            {false && bulkActionSheet === "move" && (
                <ModalShell onClose={() => setBulkActionSheet(null)} title="Move selected">
                    <div className="space-y-4 p-5 sm:p-6" style={selectionAccentStyle}>
                        <div
                            className="rounded-2xl border bg-black/25 p-4"
                            style={{
                                borderColor: `${selectionAccent}55`,
                                boxShadow: `0 0 28px ${selectionAccent}18`,
                            }}
                        >
                            <p className="text-sm font-black text-white">{selectedItemCount} selected</p>
                            <p className="mt-1 text-xs font-semibold text-neutral-500">Choose where these items should live.</p>
                        </div>
                        <div className="grid max-h-72 gap-2 overflow-y-auto pr-1">
                            <button
                                type="button"
                                onClick={() => setBulkFolder("")}
                                className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left text-sm font-bold transition ${
                                    bulkFolder === ""
                                        ? "bg-black/55 text-white"
                                        : "border-neutral-800 bg-black/35 text-neutral-300 hover:border-neutral-600"
                                }`}
                                style={bulkFolder === "" ? { borderColor: `${selectionAccent}bb`, boxShadow: `0 0 18px ${selectionAccent}16` } : undefined}
                            >
                                No folder
                                {bulkFolder === "" && <Check size={15} />}
                            </button>
                            {folders.map((folder) => (
                                <button
                                    type="button"
                                    key={folder.id}
                                    onClick={() => setBulkFolder(folder.id)}
                                    className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left text-sm font-bold transition ${
                                        bulkFolder === folder.id
                                            ? "bg-black/55 text-white"
                                            : "border-neutral-800 bg-black/35 text-neutral-300 hover:border-neutral-600"
                                    }`}
                                    style={bulkFolder === folder.id ? { borderColor: `${selectionAccent}bb`, boxShadow: `0 0 18px ${selectionAccent}16` } : undefined}
                                >
                                    <span className="inline-flex min-w-0 items-center gap-3">
                                        <FolderIcon size={16} style={{ color: folder.color || "#22d3ee" }} />
                                        <span className="truncate">{folder.name}</span>
                                    </span>
                                    {bulkFolder === folder.id && <Check size={15} />}
                                </button>
                            ))}
                        </div>
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <button type="button" onClick={() => setBulkActionSheet(null)} className="min-h-11 rounded-lg border border-neutral-800 px-4 text-sm font-bold text-neutral-300 hover:bg-neutral-900">
                                Cancel
                            </button>
                            <button type="button" onClick={handleBulkMove} className="min-h-11 rounded-lg px-4 text-sm font-black text-white" style={{ backgroundColor: selectionAccent, boxShadow: `0 0 22px ${selectionAccent}30` }}>
                                Move
                            </button>
                        </div>
                    </div>
                </ModalShell>
            )}

            {false && bulkActionSheet === "tag" && (
                <ModalShell onClose={() => setBulkActionSheet(null)} title="Tag selected">
                    <form
                        className="space-y-4 p-5 sm:p-6"
                        style={selectionAccentStyle}
                        onSubmit={(event) => {
                            event.preventDefault();
                            void handleBulkTag();
                        }}
                    >
                        <p className="text-sm text-neutral-500">Add tags to {selectedNotes.length} selected note{selectedNotes.length === 1 ? "" : "s"}.</p>
                        <input
                            value={bulkTags}
                            onChange={(event) => setBulkTags(event.target.value)}
                            placeholder="launch, ideas, spaces"
                            className="min-h-12 w-full rounded-xl border bg-black px-4 text-sm font-bold text-neutral-100 outline-none placeholder:text-neutral-600"
                            style={{ borderColor: `${selectionAccent}66`, boxShadow: `0 0 20px ${selectionAccent}12` }}
                            autoFocus
                        />
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <button type="button" onClick={() => setBulkActionSheet(null)} className="min-h-11 rounded-lg border border-neutral-800 px-4 text-sm font-bold text-neutral-300 hover:bg-neutral-900">
                                Cancel
                            </button>
                            <button type="submit" className="min-h-11 rounded-lg px-4 text-sm font-black text-white" style={{ backgroundColor: selectionAccent, boxShadow: `0 0 22px ${selectionAccent}30` }}>
                                Add tags
                            </button>
                        </div>
                    </form>
                </ModalShell>
            )}

            {false && bulkActionSheet === "share" && (
                <ModalShell onClose={() => setBulkActionSheet(null)} title="Share selected">
                    <form
                        className="space-y-4 p-5 sm:p-6"
                        style={selectionAccentStyle}
                        onSubmit={(event) => {
                            event.preventDefault();
                            void handleBulkShare();
                        }}
                    >
                        <p className="text-sm text-neutral-500">Grant view access to {selectedNotes.length} selected note{selectedNotes.length === 1 ? "" : "s"}.</p>
                        <input
                            value={bulkShareUsername}
                            onChange={(event) => setBulkShareUsername(event.target.value)}
                            placeholder="@username"
                            className="min-h-12 w-full rounded-xl border bg-black px-4 text-sm font-bold text-neutral-100 outline-none placeholder:text-neutral-600"
                            style={{ borderColor: `${selectionAccent}66`, boxShadow: `0 0 20px ${selectionAccent}12` }}
                            autoFocus
                        />
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <button type="button" onClick={() => setBulkActionSheet(null)} className="min-h-11 rounded-lg border border-neutral-800 px-4 text-sm font-bold text-neutral-300 hover:bg-neutral-900">
                                Cancel
                            </button>
                            <button type="submit" className="min-h-11 rounded-lg px-4 text-sm font-black text-white" style={{ backgroundColor: selectionAccent, boxShadow: `0 0 22px ${selectionAccent}30` }}>
                                Share
                            </button>
                        </div>
                    </form>
                </ModalShell>
            )}

            {pendingConfirm && (
                <ModalShell onClose={() => {
                    if (!confirmBusyRef.current) setPendingConfirm(null);
                }} title={pendingConfirm.title}>
                    <div className="p-5 sm:p-6">
                        <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl border border-rose-900 bg-rose-950/40 text-rose-300">
                            <Trash2 size={22} />
                        </div>
                        <h2 className="text-balance text-xl font-black leading-tight text-white sm:text-lg">{pendingConfirm.title}</h2>
                        <p className="mt-3 text-pretty text-sm leading-7 text-neutral-400">{pendingConfirm.message}</p>
                        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
                            <button
                                type="button"
                                onClick={() => {
                                    if (!confirmBusyRef.current) setPendingConfirm(null);
                                }}
                                disabled={confirmBusy}
                                className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-neutral-800 px-4 py-2.5 text-sm font-bold text-neutral-300 hover:bg-neutral-900 sm:w-auto"
                            >
                                Cancel
                            </button>
                            {pendingConfirm.secondaryLabel && pendingConfirm.onSecondary && (
                                <button
                                    type="button"
                                    onClick={runSecondaryConfirm}
                                    disabled={confirmBusy}
                                    className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-neutral-700 px-4 py-2.5 text-center text-sm font-bold leading-5 text-neutral-100 hover:bg-neutral-900 disabled:opacity-50 sm:w-auto sm:max-w-[180px]"
                                >
                                    {confirmBusy && <Loader2 size={15} className="animate-spin" />}
                                    {pendingConfirm.secondaryLabel}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={runConfirm}
                                disabled={confirmBusy}
                                className={`inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-center text-sm font-black leading-5 text-white disabled:opacity-50 sm:w-auto ${
                                    pendingConfirm.tone === "danger"
                                        ? "bg-rose-700 hover:bg-rose-600"
                                        : "bg-cyan-700 hover:bg-cyan-600"
                                }`}
                            >
                                {confirmBusy && <Loader2 size={15} className="animate-spin" />}
                                {pendingConfirm.confirmLabel}
                            </button>
                        </div>
                    </div>
                </ModalShell>
            )}
        </div>
    );
}
